const cardData = {
  "the-fool-tarot-card-reading-meaning": {
    slug: "the-fool-tarot-card-reading-meaning",
    name: "The Fool",
    subtitle: "New Beginnings, Innocence, Leap of Faith",
    image: "/card/the-fool-tarot-card-reading-meaning.webp",
    suit: "Major Arcana",
    description: `The Fool represents the spirit of adventure, innocence and infinite potential. Unburdened by past experiences or future fears, this card signifies a moment where you are free to pursue something new — even if you don’t know where the path leads. The Fool does not fear the unknown; rather, it trusts in the universe’s timing. Whether it's starting a new relationship, job, or life chapter, the Fool invites you to leap with joy, curiosity and openness. It encourages living in the present, letting go of hesitation and listening to your inner voice as you chart new territory.`,
    upright: [
      "New beginnings – a fresh chapter or clean slate",
      "Spontaneity – embracing adventure with childlike wonder",
      "Innocence – approaching life with purity and open-heartedness",
      "Faith – trusting the universe and your inner guidance",
      "Potential – unlimited possibilities ahead"
    ],
    reversed: [
      "Recklessness – acting without thinking of consequences",
      "Naivety – ignoring important warnings or red flags",
      "Fear of the unknown – resisting growth due to insecurity",
      "Disconnection – lack of direction or purpose",
      "Missed opportunities – failure to seize the moment due to hesitation"
    ],
    reading: `When The Fool graces your reading, it is not merely suggesting change — it is summoning you to awaken.

You are being asked to stand barefoot at the edge of your life as you know it, to feel the earth humming beneath you and the unknown beckoning beyond. The Fool doesn’t offer a map — it offers a moment: sacred, raw and filled with possibility.

This card appears when logic no longer leads, when the heart whispers louder than reason and when the soul is ready to walk paths the mind cannot comprehend. If you’ve been feeling stuck, uncertain, or afraid to make a move — The Fool reminds you that freedom lives on the other side of fear.

Yet this is not recklessness. This is spiritual trust. A surrendering to divine timing, to instinct and to the call of something greater than comfort. You may not know the full picture, but you know enough to begin. And that is enough.

Ask yourself:
- What have I been hesitating to begin, not because it’s wrong, but because it’s unfamiliar?
- Where have I allowed fear or practicality to override the truth of my spirit?
- Can I trust that the Universe meets me halfway when I walk in alignment with my calling?

When The Fool appears, the old story is finished. The next one begins not with certainty, but with courage — and your willingness to say yes to the mystery.`,
    mirroring: ["the-magician-tarot-card-reading-meaning", "the-hermit-tarot-card-reading-meaning", "the-world-tarot-card-reading-meaning"]
  },

"the-magician-tarot-card-reading-meaning": {
  slug: "the-magician-tarot-card-reading-meaning",
  name: "The Magician",
  subtitle: "Manifestation, Willpower, Creation",
  image: "/card/the-magician-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The Magician stands at the threshold between the spiritual and material worlds, channeling divine energy into tangible form. This card speaks of your ability to create your reality through focused intent, inspired action and confidence. Everything you need is already at your fingertips — tools, skills and insight. The Magician reminds you that with alignment and clarity, manifestation is not a miracle, but a discipline.`,
  upright: [
    "Manifestation – bringing desires into reality with clarity",
    "Willpower – focused intention backed by inspired action",
    "Skill – using your natural talents and tools wisely",
    "Alignment – balancing spiritual guidance with grounded steps",
    "Initiative – taking charge of your path with confidence"
  ],
  reversed: [
    "Manipulation – using skills for selfish or deceptive ends",
    "Scattered focus – lacking direction or concentration",
    "Missed potential – not using your resources effectively",
    "Illusions – believing in false power or superficial charm",
    "Insecurity – doubting your abilities despite having the tools"
  ],
  reading: `When The Magician appears, it is a thunderclap of purpose echoing through your life.

This card is a mirror reflecting your own divine potential — not just to dream, but to *do*. The universe has placed tools before you: your intellect, your intuition, your passion, your practical know-how. Now, the question is not *if* you are ready, but whether you are willing to begin.

The Magician doesn’t wait for perfect timing; it *creates* it. It calls you to align your inner truth with outer action — to speak what you mean and move in the direction of what you seek. Be mindful, though: power used without ethics turns to trickery. Channel your magic through clarity, humility and heart.

Ask yourself:
- Am I using all the tools available to me right now?
- Where am I holding back from taking inspired action?
- What would happen if I acted like the universe is co-creating with me?

The Magician is your permission slip — now shape the unseen into the seen.`,
  mirroring: ["the-fool-tarot-card-reading-meaning", "the-empress-tarot-card-reading-meaning", "the-hierophant-tarot-card-reading-meaning"]
},

"the-high-priestess-tarot-card-reading-meaning": {
  slug: "the-high-priestess-tarot-card-reading-meaning",
  name: "The High Priestess",
  subtitle: "Mystery, Intuition, Sacred Knowledge",
  image: "/card/the-high-priestess-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The High Priestess sits silently between the pillars of light and shadow, guarding the threshold of hidden truths. She does not speak — she invites. Her presence is a reminder that not all answers lie in logic. Some truths are whispered within, in dreams, symbols and subtle knowing. She represents your inner voice, the mysteries of the subconscious and the deep well of wisdom accessible through stillness.`,
  upright: [
    "Intuition – trusting your inner voice over external noise",
    "Mystery – accepting that some truths unfold in divine timing",
    "Silence – stepping back to observe before acting",
    "Wisdom – accessing deep knowing from within",
    "Spiritual insight – attuning to dreams and sacred symbols"
  ],
  reversed: [
    "Secrets – things hidden or withheld, within or without",
    "Disconnection – ignoring your inner guidance",
    "Confusion – unclear signs or clouded perception",
    "Repression – holding back truths or feelings",
    "Mistrust – doubting your spiritual or intuitive senses"
  ],
  reading: `When The High Priestess enters your reading, she brings no noise — only presence.

She calls you into the temple of your own inner world, asking you to sit with the silence, to listen for what is not said aloud. This card does not urge you to act, but to *feel*, to *sense*, to *know* without proof. In a world of constant noise, she is the sacred pause that restores clarity.

You may be on the edge of a truth — but it is not yet time to force it. Trust the rhythm of revelation. Honor your gut feelings and your nighttime visions. What seems invisible may already be forming in your soul’s awareness.

Ask yourself:
- What have I been sensing, but haven’t fully acknowledged?
- Where might I need to pause before seeking external answers?
- What wisdom is ready to rise from within if I simply listen?

The High Priestess holds the veil — but she will lift it when you’re ready to see. Until then, remain still and trust the knowing that lives in silence.`,
  mirroring: ["the-hermit-tarot-card-reading-meaning", "the-moon-tarot-card-reading-meaning", "the-hanged-man-tarot-card-reading-meaning"]
},

"the-empress-tarot-card-reading-meaning": {
  slug: "the-empress-tarot-card-reading-meaning",
  name: "The Empress",
  subtitle: "Fertility, Nurturing, Abundance",
  image: "/card/the-empress-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The Empress embodies the essence of creation and the rhythm of nature’s cycles. She is the fertile womb of ideas, art, love and life itself. Rooted in sensuality, comfort and care, this card speaks to nurturing yourself and others from a place of deep love. She reminds you that beauty grows best when it is nourished consistently and gently, not rushed or forced.`,
  upright: [
    "Creativity – birthing new ideas or projects",
    "Abundance – attracting wealth, love and comfort",
    "Nurturing – caring for yourself or others with gentleness",
    "Fertility – potential for growth in any form",
    "Sensuality – connecting to the body and physical pleasure"
  ],
  reversed: [
    "Neglect – giving too much to others while ignoring your needs",
    "Creative block – stagnation or burnout",
    "Dependence – overreliance on comfort or others’ validation",
    "Emotional smothering – overwhelming others with care",
    "Disconnected – losing touch with nature or your body"
  ],
  reading: `When The Empress appears, she does not knock — she opens the door wide and invites you to come home to yourself.

This card is a sanctuary: a reminder that your worth is not in productivity, but in presence. The Empress doesn’t rush — she receives, she nurtures, she allows. Whether you're planting seeds of a dream, or caring for someone dear, she asks you to do so with tenderness and trust in divine timing.

Your senses are sacred tools. Indulge in beauty. Rest without guilt. Love without reserve. This card is an invitation to return to softness, to reclaim your creativity and to honor the cycles of birth, bloom and rest.

Ask yourself:
- Am I nurturing myself as deeply as I nurture others?
- What creative or emotional seed wants more attention?
- How can I embody abundance in a grounded, graceful way?

The Empress reminds you that to grow anything worthwhile, you must first create a fertile space within yourself.`,
  mirroring: ["the-magician-tarot-card-reading-meaning", "the-moon-tarot-card-reading-meaning", "the-star-tarot-card-reading-meaning"]
},

"the-emperor-tarot-card-reading-meaning": {
  slug: "the-emperor-tarot-card-reading-meaning",
  name: "The Emperor",
  subtitle: "Structure, Authority, Stability",
  image: "/card/the-emperor-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The Emperor sits upon a throne of stone, embodying leadership, logic and grounded power. He is the architect of order, the protector of legacy and the container of potential. When this card appears, it invites you to take responsibility — not through force, but through clarity, discipline and wisdom. True authority is not control — it’s presence, consistency and earned respect.`,
  upright: [
    "Structure – creating systems that support your vision",
    "Authority – stepping into responsible leadership",
    "Discipline – establishing boundaries and focus",
    "Security – building stability and long-term foundations",
    "Legacy – taking actions with future consequences in mind"
  ],
  reversed: [
    "Rigidity – being too controlling or inflexible",
    "Abuse of power – dominating rather than guiding",
    "Instability – weak foundations or lack of structure",
    "Resistance – rebelling against necessary discipline",
    "Ego – leading from pride rather than purpose"
  ],
  reading: `When The Emperor arrives, he is not here to charm you — he’s here to challenge you.

This card calls forth the part of you that must take command of your circumstances, make hard decisions and build something that lasts. It’s time to root your energy in action — not just dreams. The Emperor shows up when stability must be chosen over chaos, when duty must outweigh impulse.

But beware: power is not license. Real strength lies in self-mastery. This card invites you to lead with integrity, to protect without possessiveness and to create structures that uplift, not restrict.

Ask yourself:
- Where do I need to build stronger foundations in my life?
- How can I lead with integrity and clarity?
- Am I using power wisely — or is it using me?

The Emperor asks you to rise — not as a ruler over others, but as the sovereign of your own path.`,
  mirroring: ["the-empress-tarot-card-reading-meaning", "justice-tarot-card-reading-meaning", "the-hierophant-tarot-card-reading-meaning"]
},

"the-hierophant-tarot-card-reading-meaning": {
  slug: "the-hierophant-tarot-card-reading-meaning",
  name: "The Hierophant",
  subtitle: "Tradition, Wisdom, Spiritual Authority",
  image: "/card/the-hierophant-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The Hierophant is the guardian of sacred knowledge passed down through time. He represents tradition, belief systems, mentors and rituals that connect us to meaning beyond the material. This card asks you to examine the frameworks that guide your life — are they aligned with your truth, or inherited without question? The Hierophant invites you to learn, reflect and discern.`,
  upright: [
    "Spiritual learning – seeking guidance or deeper meaning",
    "Tradition – honoring rituals, teachings, or structure",
    "Mentorship – finding or becoming a teacher or guide",
    "Community – connecting to shared beliefs or values",
    "Integrity – aligning personal actions with ethical principles"
  ],
  reversed: [
    "Dogma – blindly following outdated or rigid beliefs",
    "Rebellion – resisting guidance that could be beneficial",
    "Disconnection – feeling spiritually isolated",
    "Inauthenticity – living by others’ rules instead of your own",
    "Confusion – unsure of your beliefs or moral compass"
  ],
  reading: `When The Hierophant appears, he does not instruct — he reflects.

This card is not about blind obedience, but about conscious alignment. Are you walking a path that resonates with your soul — or simply following a map drawn by others? The Hierophant honors wisdom traditions, but he also invites questioning. He teaches, but he also listens.

This moment may call for mentorship, or a return to spiritual practice. Or perhaps it's time to become the teacher — to pass on what you’ve learned. Examine your beliefs. Engage with sacred texts, meditations, or elders. Let ancient wisdom illuminate your modern path.

Ask yourself:
- What values or traditions truly resonate with me?
- Who or what can guide me toward deeper truth?
- Where might I be conforming rather than aligning?

The Hierophant reminds you: knowledge is sacred, but only when it awakens the heart as well as the mind.`,
  mirroring: ["the-magician-tarot-card-reading-meaning", "the-emperor-tarot-card-reading-meaning", "temperance-tarot-card-reading-meaning"]
},
"the-lovers-tarot-card-reading-meaning": {
  slug: "the-lovers-tarot-card-reading-meaning",
  name: "The Lovers",
  subtitle: "Union, Choice, Divine Alignment",
  image: "/card/the-lovers-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The Lovers represents a sacred union — not just between two people, but between your soul and your truth. This card embodies harmony, mutual attraction and the divine orchestration of love and purpose. At its heart, it’s not merely about romance, but about alignment. It asks: Are your choices guided by love or fear? The Lovers card calls you to integrate the fragmented parts of yourself and make decisions from a place of honesty and soulful connection.`,
  upright: [
    "Sacred union – a meaningful connection or relationship",
    "Alignment – choices made in harmony with your values",
    "Attraction – magnetic energy pulling you toward a person or purpose",
    "Commitment – deepening bonds through trust and honesty",
    "Moral crossroads – a decision that defines your integrity"
  ],
  reversed: [
    "Disharmony – imbalance in relationships or within the self",
    "Conflict – values misaligned or difficult decisions avoided",
    "Temptation – being swayed by surface-level desires",
    "Indecision – fear of making the wrong choice",
    "Separation – a loss of connection or mutual understanding"
  ],
  reading: `When The Lovers appear in a reading, you are being asked to look beyond the surface of a relationship or choice. This is not a card of fleeting emotions or shallow attraction — it is a mirror reflecting your deepest values and the agreements you’ve made with your own heart. Whether romantic, platonic, or spiritual, this card signals a crossroads that requires truth. Not just emotional truth, but soul truth. What you choose now will shape who you become.

The Lovers do not simply represent passion; they represent alignment. That feeling when something or someone fits into your life with sacred resonance. It may feel like destiny, but it still demands effort, courage and self-awareness. This card calls you to choose with integrity — to ask not only what you want, but what is worthy of your heart. Love, in its highest form, is not just a feeling. It’s a choice made over and over again, even when it’s hard.

There may be temptation pulling you in multiple directions — a battle between the familiar and the transformative, the easy and the right. The Lovers remind you that true harmony doesn’t ask you to lose yourself; it invites you to become more fully who you are. Choose not just who you love, but how you love — and how you show up for love in all areas of your life.

Ask yourself:
- Am I choosing from love or fear?
- Where in my life am I being asked to realign with my deepest values?
- What parts of me need to be integrated before I can truly connect with another?

The Lovers invite you into union — not just with another, but with the truth of your soul.`,
  mirroring: ["the-hierophant-tarot-card-reading-meaning", "temperance-tarot-card-reading-meaning", "two-of-cups-tarot-card-reading-meaning"]
},

"the-chariot-tarot-card-reading-meaning": {
  slug: "the-chariot-tarot-card-reading-meaning",
  name: "The Chariot",
  subtitle: "Willpower, Triumph, Direction",
  image: "/card/the-chariot-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The Chariot symbolizes momentum born of discipline and determination. It is the force that arises when your will aligns with your purpose. Where there was once hesitation, there is now forward motion — not reckless, but deliberate. This card signals that victory is within reach, but only if you stay committed to your path and steer with both control and inner conviction.`,
  upright: [
    "Determination – a strong will pushing past obstacles",
    "Victory – triumph through discipline and focus",
    "Control – steering your life with conscious direction",
    "Courage – facing challenges with boldness and strength",
    "Progress – rapid forward momentum aligned with purpose"
  ],
  reversed: [
    "Lack of direction – feeling pulled in opposite directions",
    "Forcefulness – trying to control everything without flow",
    "Inner conflict – will and heart misaligned",
    "Delay – progress hindered by self-doubt or resistance",
    "Defeat – losing focus or giving up before the breakthrough"
  ],
  reading: `When The Chariot charges into your reading, it brings a thunderous message: you are not lost, you are becoming. This is the card of the warrior spirit — not the kind who fights with fists, but the one who battles chaos with clarity and rides storms with unwavering resolve. The path before you is not smooth, but it is yours to claim. Your power now lies in forward motion and in trusting the direction your soul is pulling you toward.

The two sphinxes — or horses — often shown on the card, represent opposing forces. They may be ambition and fear, desire and duty, logic and emotion. The Chariot teaches that success does not come from choosing one over the other, but from holding the reins of both. It is your inner alignment that brings outer triumph. This moment calls for a decision, a commitment and the resolve to drive through distraction without losing your center.

This card does not promise ease. It demands focus. It reminds you that mastery isn’t about domination — it’s about devotion. Devotion to the journey, to your growth, to the version of yourself that refuses to abandon the mission even when it’s hard. Let this card be your war cry: I will not be ruled by circumstance. I steer my fate with both hands.

Ask yourself:
- Where am I being asked to take the reins of my own destiny?
- What conflicting forces must I reconcile to move forward with strength?
- Am I chasing external success, or aligned progress?

The Chariot arrives to declare that momentum is sacred — and when guided by purpose, it becomes unstoppable.`,
  mirroring: ["strength-tarot-card-reading-meaning", "six-of-wands-tarot-card-reading-meaning", "two-of-wands-tarot-card-reading-meaning"]
},

"strength-tarot-card-reading-meaning": {
  slug: "strength-tarot-card-reading-meaning",
  name: "Strength",
  subtitle: "Inner Power, Compassion, Resilience",
  image: "/card/strength-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `Strength is not brute force — it is calm resolve, quiet courage and the ability to tame the wild within. This card represents emotional mastery, patience and the graceful balance between gentleness and power. It teaches that true courage lies in meeting fear, anger, or pain with compassion rather than control.`,
  upright: [
    "Inner strength – tapping into courage during adversity",
    "Compassion – responding to challenges with grace",
    "Resilience – quiet power that endures difficulty",
    "Self-control – managing emotions with patience and presence",
    "Healing – turning wounds into wisdom"
  ],
  reversed: [
    "Self-doubt – feeling inadequate or emotionally fragile",
    "Impatience – reacting instead of responding",
    "Inner turmoil – losing grip over intense emotions",
    "Abuse of power – forcing outcomes without empathy",
    "Fear – avoiding confrontation with one’s shadow"
  ],
  reading: `When Strength appears, it does not roar. It whispers — softly, firmly — reminding you that tenderness can be more powerful than fury. This card invites you to look not at how loudly you resist life’s storms, but how bravely you sit with them. True strength is not about how much you can carry, but how much peace you can keep while carrying it. You are being called to stand rooted in love — not just for others, but for your own wounded parts.

The lion in this card does not need to be caged — it is tamed through presence. So too, the parts of you that feel wild, angry, fearful, or ashamed are not enemies to be conquered. They are energies to be met with compassion. Strength asks: Can you hold your pain with the same hands you use to hold your joy? Can you stay soft, even when life hardens around you?

This is the moment to gather yourself inward. To stop pushing, stop forcing and instead cultivate quiet resilience. If you’ve been tested, triggered, or thrown off-center — come back to your breath. You are stronger than you know, not because you win every battle, but because you have the heart to keep walking and the grace to do it kindly.

Ask yourself:
- What part of me is asking to be tamed through love, not fear?
- How can I show myself compassion in this challenge?
- Am I measuring strength by appearances, or inner peace?

Strength appears when your softness becomes your superpower — and your endurance, a sacred act of self-respect.`
,
  mirroring: ["the-chariot-tarot-card-reading-meaning", "the-empress-tarot-card-reading-meaning", "nine-of-wands-tarot-card-reading-meaning"]
},

"the-hermit-tarot-card-reading-meaning": {
  slug: "the-hermit-tarot-card-reading-meaning",
  name: "The Hermit",
  subtitle: "Introspection, Solitude, Inner Guidance",
  image: "/card/the-hermit-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The Hermit is the seeker who steps away from the world not out of fear, but to find deeper truth. This card signifies a period of introspection, retreat, or spiritual searching. It calls you to look within — to listen for your own wisdom rather than the noise of others.`,
  upright: [
    "Solitude – choosing time alone for reflection",
    "Inner guidance – trusting your own insight",
    "Spiritual quest – searching for deeper meaning",
    "Withdrawal – stepping back to recalibrate",
    "Truth – uncovering authentic answers within"
  ],
  reversed: [
    "Isolation – feeling lost or emotionally withdrawn",
    "Loneliness – resisting connection with others",
    "Avoidance – using solitude to escape problems",
    "Disconnection – losing touch with your inner light",
    "Stagnation – spiritual or emotional inertia"
  ],
  reading: `When The Hermit appears, it’s an invitation to pause — not out of weakness, but out of wisdom. You are being called to step away from external validation and return to your sacred cave. There, in the stillness, lives your truth. The answers you seek may not be loud or immediate, but they are waiting — beneath the noise, behind the distractions, within you.

This card honors those moments in life when the path becomes unclear and no one's voice but your own can guide you. It reminds you that solitude is not loneliness — it is sanctuary. And from this space, your light is rekindled. The Hermit walks with a lantern not just to find the way, but to *become* the light for others once again.

If you’ve been overwhelmed, disconnected, or uncertain, The Hermit asks you to turn inward — not to hide, but to rediscover. Let go of others’ expectations and remember your inner compass. Silence isn’t absence — it’s presence in its purest form.

Ask yourself:
- Where in my life am I craving silence or space?
- What inner wisdom have I been ignoring?
- Am I seeking answers outward when I already carry them within?

When The Hermit appears, it’s not the world that needs changing — it’s the way you listen to your own light.`
,
  mirroring: ["the-high-priestess-tarot-card-reading-meaning", "four-of-swords-tarot-card-reading-meaning", "the-hanged-man-tarot-card-reading-meaning"]
},

"wheel-of-fortune-tarot-card-reading-meaning": {
  slug: "wheel-of-fortune-tarot-card-reading-meaning",
  name: "Wheel of Fortune",
  subtitle: "Cycles, Destiny, Turning Points",
  image: "/card/wheel-of-fortune-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The Wheel of Fortune reminds you that nothing stays the same. It is the sacred turning of life’s wheel — where fate, choice and unseen forces collide. This card represents change, karmic lessons and the cyclical nature of fortune and challenge.`,
  upright: [
    "Destiny – events aligning beyond your control",
    "Cycles – beginnings and endings in motion",
    "Opportunity – a window to shift your path",
    "Karma – reaping what has been sown",
    "Turning point – a significant life transition"
  ],
  reversed: [
    "Resistance – struggling against change",
    "Stagnation – feeling stuck in repeating patterns",
    "Bad timing – delays or unexpected setbacks",
    "Lack of control – feeling at the mercy of fate",
    "Missed chances – ignoring signs of transition"
  ],
  reading: `The Wheel of Fortune turns whether you're ready or not. Its message is simple but profound: life is always in motion. You may feel on top of the world, or buried beneath its weight — but either way, this too shall pass. Your task is not to control the wheel, but to center yourself within it. Breathe into the uncertainty. Trust the cycle.

This card appears at moments of great shift — some welcome, some disruptive. But all sacred. The Wheel teaches surrender, not as a loss of power, but as alignment with a greater rhythm. When you resist its turn, you create friction. When you embrace it, even the hardest changes reveal their purpose.

Ask yourself not what you can *prevent*, but what you are being *prepared* for. The Wheel spins you into your next chapter, whether you feel ready or not. The question is — will you ride the wave or cling to the shore?

Ask yourself:
- Where in my life is change trying to happen?
- Am I trusting the flow of events or fighting them?
- What pattern keeps repeating — and what is it trying to teach me?

When the Wheel turns, it is not punishment or reward — it is invitation. Let go. Lean in. Trust the spin.`
,
  mirroring: ["death-tarot-card-reading-meaning", "the-fool-tarot-card-reading-meaning", "judgement-tarot-card-reading-meaning"]
},

"justice-tarot-card-reading-meaning": {
  slug: "justice-tarot-card-reading-meaning",
  name: "Justice",
  subtitle: "Truth, Fairness, Accountability",
  image: "/card/justice-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `Justice is the guardian of truth and balance. She carries both the sword and the scales — discernment and harmony. This card asks for integrity, responsibility and clarity. It signifies karmic reckoning, legal matters, or a moment of decision that must be made with honesty.`,
  upright: [
    "Truth – clarity emerging from facts",
    "Accountability – taking ownership of actions",
    "Fairness – balance between logic and compassion",
    "Decisions – weighing outcomes carefully",
    "Karma – consequences of past choices"
  ],
  reversed: [
    "Dishonesty – withholding truth or self-deceit",
    "Injustice – unfair treatment or imbalance",
    "Avoidance – refusing to take responsibility",
    "Corruption – misuse of power or bias",
    "Unresolved – issues lacking closure or clarity"
  ],
  reading: `Justice does not ask for perfection — she asks for truth. When she appears in a reading, you are being summoned to examine where clarity, fairness, or integrity must rise. Perhaps you are being called to make a difficult decision — or to reckon with one already made. Either way, this is a time for honesty, both with others and with yourself.

This card does not reward emotion over logic, nor logic over heart — it seeks alignment. If you have been wronged, Justice brings a promise of balance. If you have erred, it brings a chance to make things right. But one thing is certain: the scales will not be rushed. The universe is asking you to slow down and weigh things with care.

You hold the sword now — but it is not for harm. It is for clarity. Cut through illusion. Name your truth. Take ownership, even if it stings. When you stand in truth, you are never powerless.

Ask yourself:
- Am I avoiding accountability in any part of my life?
- What truth must be spoken — even if uncomfortable?
- How can I restore balance in this situation?

Justice stands firm, not with judgment, but with purpose. Let truth be your guide and the scales will find their center.`
,
  mirroring: ["temperance-tarot-card-reading-meaning", "the-hierophant-tarot-card-reading-meaning", "two-of-swords-tarot-card-reading-meaning"]
},

"the-hanged-man-tarot-card-reading-meaning": {
  slug: "the-hanged-man-tarot-card-reading-meaning",
  name: "The Hanged Man",
  subtitle: "Surrender, Perspective, Pause",
  image: "/card/the-hanged-man-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The Hanged Man hangs not in punishment, but in purpose. This card symbolizes a sacred pause — a moment where progress halts so that deeper wisdom can surface. It asks for surrender, new perspectives and trust in divine timing.`,
  upright: [
    "Surrender – releasing control over outcomes",
    "Pause – intentional delay for reflection",
    "Perspective – seeing things from a new angle",
    "Letting go – sacrificing ego for wisdom",
    "Enlightenment – stillness leading to awakening"
  ],
  reversed: [
    "Resistance – fighting stillness or change",
    "Stagnation – delay without purpose",
    "Martyrdom – sacrificing too much or too often",
    "Impatience – frustration with divine timing",
    "Avoidance – not facing what must be seen"
  ],
  reading: `The Hanged Man comes to stop time. To turn your world upside down — not to confuse, but to clarify. If life feels like it’s at a standstill, this card says: Good. Now you can see what you were too busy to notice. Now you can *feel* what rushing kept you from feeling. This pause is not punishment — it’s preparation.

In this suspended space, you are being invited to unlearn. To drop old stories. To yield, not because you’ve given up, but because you are giving over — to insight, to spirit, to truth. Something must be seen differently before you can move forward. This is the space between stories. Honor it.

If you’ve been pushing, striving, or demanding resolution — stop. Breathe. Listen. Surrender does not mean you’ve lost. It means you are wise enough to wait for real clarity.

Ask yourself:
- Where am I being asked to release control?
- What truth can I only see by pausing?
- Am I willing to trust this delay as sacred?

The Hanged Man is the stillness before transformation. Let it shape you gently. Then rise with a vision that was once upside down — and is now profoundly clear.`
,
  mirroring: ["the-hermit-tarot-card-reading-meaning", "death-tarot-card-reading-meaning", "four-of-swords-tarot-card-reading-meaning"]
},
"death-tarot-card-reading-meaning": {
  slug: "death-tarot-card-reading-meaning",
  name: "Death",
  subtitle: "Endings, Transformation, Renewal",
  image: "/card/death-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `Despite its name, the Death card is not a symbol of literal demise, but of deep transformation and necessary endings. It signals a powerful clearing — a letting go of what has run its course so something new can be born. Death asks you to surrender what no longer serves you, even if it once defined you.`,
  upright: [
    "Transformation – major shifts and inner rebirth",
    "Endings – releasing people, patterns, or chapters",
    "Letting go – surrendering attachments gracefully",
    "Renewal – space for new growth to emerge",
    "Liberation – freedom from stagnant energy"
  ],
  reversed: [
    "Resistance – fear of change or clinging to the past",
    "Stagnation – staying in dead-end situations",
    "Emotional block – refusing to grieve or move on",
    "Avoidance – not facing necessary endings",
    "Delayed transformation – growth held back by fear"
  ],
  reading: `When Death arrives, it comes as a whisper, not a threat. It nudges gently, asking what you’re still carrying that has already expired in your soul. This card appears when something old — an identity, relationship, belief, or cycle — must be laid to rest. And though it may bring discomfort or grief, it also brings truth: that you are evolving and life is asking you to unclench your grip.

The energy of Death is not cruel, but compassionate. It removes what no longer belongs so your essence can breathe again. You may feel loss, but beneath it, a quiet liberation grows. Something new is forming — not in haste, but with reverence. The pain of death is the pain of shedding, of stepping naked into your next chapter without knowing what it holds. And still, it calls you forward.

This is your invitation to grieve — but also to release. There is no rebirth without the death of what came before. If you’ve been afraid to end something — a toxic habit, a lifeless job, a fading attachment — Death is the mirror showing that your spirit already knows. Trust that in letting go, you will find yourself again — unburdened, renewed and closer to your truth.

Ask yourself:
- What chapter in my life has reached its natural end?
- Where am I clinging to what no longer serves me?
- Can I trust that letting go will lead to deeper alignment?

Death appears when you’re ready to bury the old story — and rise from the roots of what remains.`,

  mirroring: ["the-tower-tarot-card-reading-meaning", "the-hanged-man-tarot-card-reading-meaning", "judgement-tarot-card-reading-meaning"]
},

"temperance-tarot-card-reading-meaning": {
  slug: "temperance-tarot-card-reading-meaning",
  name: "Temperance",
  subtitle: "Balance, Harmony, Divine Timing",
  image: "/card/temperance-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `Temperance is the sacred art of moderation, the alchemy of blending opposites to create wholeness. It invites you to walk the middle path, integrating extremes with grace and patience. This card speaks of spiritual maturity — the ability to hold tension without rushing, forcing, or breaking.`,
  upright: [
    "Harmony – finding inner peace and equilibrium",
    "Balance – managing emotions and priorities well",
    "Moderation – avoiding extremes and excess",
    "Healing – integrating body, mind and spirit",
    "Divine timing – trusting the process to unfold"
  ],
  reversed: [
    "Imbalance – feeling off-center or scattered",
    "Excess – overindulging or overextending",
    "Conflict – resisting inner or outer integration",
    "Impatience – trying to force outcomes prematurely",
    "Disconnection – neglecting spiritual alignment"
  ],
  reading: `Temperance arrives like a gentle breath, reminding you that not everything must be rushed or controlled. It’s a card of alchemy — where your inner opposites, your chaos and calm, your grief and growth, can be blended with mindful grace. When life pulls you toward extremes, Temperance brings you back to center, to softness, to the rhythm that is uniquely yours.

This card encourages you to become the sacred container — holding both fire and water without letting either overflow. It’s a time to let life unfold, to trust the unseen forces at play and to surrender urgency for presence. Temperance is the medicine for burnout, the balm for conflict and the teacher of divine timing. Let it slow your breath and guide you back to your highest balance.

You are in a space where healing comes not from doing more, but from being still, from choosing measured responses over reactive ones. Whether you're rebuilding after a storm or seeking clarity, let Temperance guide you with its quiet wisdom. The middle path isn't a compromise — it's a sacred choice.

Ask yourself:
- Where am I being pulled to extremes in thought, emotion, or behavior?
- What would balance feel like in my current situation?
- Can I trust that divine timing is wiser than my own urgency?

Temperance appears when the soul needs peace, not progress — and when your next step is to blend, not break.`,

  mirroring: ["the-star-tarot-card-reading-meaning", "justice-tarot-card-reading-meaning", "the-high-priestess-tarot-card-reading-meaning"]
},

"the-devil-tarot-card-reading-meaning": {
  slug: "the-devil-tarot-card-reading-meaning",
  name: "The Devil",
  subtitle: "Temptation, Attachment, Shadow Work",
  image: "/card/the-devil-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The Devil reveals where you are bound — not by chains, but by choices. It speaks of temptation, illusion and the unconscious agreements that keep you small. This card asks you to confront your shadow, your vices and your fear-driven patterns with radical honesty.`,
  upright: [
    "Temptation – indulgence or obsession clouding clarity",
    "Attachment – unhealthy relationships or habits",
    "Illusion – mistaking bondage for freedom",
    "Materialism – overfocus on pleasure, wealth, or control",
    "Shadow work – facing inner fears or denied parts of self"
  ],
  reversed: [
    "Liberation – breaking free from toxic cycles",
    "Awareness – recognizing illusion and reclaiming power",
    "Detachment – releasing what controls you",
    "Inner healing – integrating the shadow self",
    "Empowerment – choosing freedom over fear"
  ],
  reading: `When The Devil emerges, it does not shame — it illuminates. It brings to light the chains you didn’t know you were wearing. These are not always external forces. More often, they are inner patterns: addictions, fears, false beliefs, or unhealthy attachments that keep you looping in cycles of self-sabotage.

This card dares you to confront what you’ve denied. It offers no judgment, only reflection — a mirror to the places where desire has turned to dependence and comfort has become a cage. The Devil isn’t evil; it’s unconscious power misused. And through awareness, those chains can be broken, not with violence, but with clarity.

You are being called to choose yourself — not the easy way, not the escape, but the truth. When you reclaim your power, the Devil loses its grip. This card doesn’t ask for fear, but for fierce self-honesty. You already hold the key.

Ask yourself:
- What habits or beliefs are silently controlling me?
- Where have I confused pleasure with freedom?
- What truth am I avoiding in order to stay comfortable?

The Devil appears when you are ready to reclaim your light — by meeting the darkness with eyes wide open.`,

  mirroring: ["the-tower-tarot-card-reading-meaning", "the-moon-tarot-card-reading-meaning", "death-tarot-card-reading-meaning"]
},

"the-tower-tarot-card-reading-meaning": {
  slug: "the-tower-tarot-card-reading-meaning",
  name: "The Tower",
  subtitle: "Revelation, Collapse, Awakening",
  image: "/card/the-tower-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The Tower is the sudden lightning strike that shatters illusion. It represents chaos, upheaval and unexpected revelation — not to punish, but to liberate. When false foundations fall, what remains is what’s true.`,
  upright: [
    "Sudden change – events that shake your sense of security",
    "Revelation – truth breaking through denial or illusion",
    "Collapse – the fall of false structures, beliefs, or roles",
    "Breakthrough – profound awakening after disruption",
    "Liberation – freedom from what was never real"
  ],
  reversed: [
    "Resistance – avoiding inevitable change",
    "Delayed collapse – holding up what’s already broken",
    "Fear of loss – clinging to comfort over truth",
    "Emotional chaos – inner storms needing release",
    "Stunted growth – failing to grow because of fear"
  ],
  reading: `The Tower does not come gently — it comes to free you. When you’ve built your life upon shaky foundations — illusions, roles, comforts, ego — the Tower appears as divine disruption. Though terrifying, it clears away the inauthentic so the real can rise. It is not destruction for cruelty’s sake, but for sacred revelation.

This is the storm that shows you what matters. What falls away was never meant to last. The Tower strips you of illusion, forcing clarity in places you’ve long avoided. And though the collapse may be painful, it is also permission — to rebuild with integrity, with truth, with depth. This card is the divine reset.

Let go. Let it fall. Let the truth stand bare. This is where true power begins — not in resisting chaos, but in rising from it.

Ask yourself:
- What truths have I been avoiding to maintain comfort?
- Where have I built my identity on false foundations?
- What would it feel like to trust the collapse?

The Tower appears not to destroy you — but to set your soul free.`,

  mirroring: ["the-devil-tarot-card-reading-meaning", "death-tarot-card-reading-meaning", "judgement-tarot-card-reading-meaning"]
},

"the-star-tarot-card-reading-meaning": {
  slug: "the-star-tarot-card-reading-meaning",
  name: "The Star",
  subtitle: "Hope, Renewal, Divine Light",
  image: "/card/the-star-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The Star is a celestial balm — the quiet after the storm. It brings healing, inspiration and the return of faith. This card reflects a time when your soul is being nourished by grace and a renewed sense of hope begins to shimmer on the horizon.`,
  upright: [
    "Hope – renewed belief in your path and future",
    "Healing – emotional or spiritual restoration",
    "Inspiration – feeling guided by inner or divine light",
    "Clarity – seeing truth through intuition and peace",
    "Faith – trusting the journey ahead"
  ],
  reversed: [
    "Despair – losing sight of purpose or direction",
    "Disconnection – feeling numb or spiritually blocked",
    "Self-doubt – struggling to trust yourself or the future",
    "Hopelessness – temporary loss of clarity or motivation",
    "Emotional fatigue – in need of rest and renewal"
  ],
  reading: `The Star appears when your spirit is thirsty for hope. It comes after hardship — often after the Tower — to soothe, to illuminate and to remind you that healing is not only possible, but already unfolding. Even in darkness, your inner light has never gone out. This card asks you to reconnect with that soft flicker of faith.

Let yourself rest. Let your heart breathe again. The Star doesn’t ask you to leap or push, but to receive — beauty, grace, inspiration. Whether through nature, art, prayer, or silence, find what renews your soul and let it wash over you. This is a sacred pause where your dreams can be reimagined.

You are not alone. You are being guided — not by force, but by stars. Let them remind you of your divine essence and trust that healing will lead you home.

Ask yourself:
- What is softly guiding me forward, even when I feel lost?
- Where am I being invited to rest and heal?
- Can I trust that something beautiful is unfolding beneath the surface?

The Star appears when your soul is ready to remember its own light.`,

  mirroring: ["temperance-tarot-card-reading-meaning", "the-moon-tarot-card-reading-meaning", "the-fool-tarot-card-reading-meaning"]
},

"the-moon-tarot-card-reading-meaning": {
  slug: "the-moon-tarot-card-reading-meaning",
  name: "The Moon",
  subtitle: "Illusion, Intuition, Shadow Work",
  image: "/card/the-moon-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The Moon casts a mysterious light that blurs the boundaries between truth and illusion. This card speaks to the unconscious mind, dreams and fears that live beneath the surface. It invites you into shadow work — to explore the hidden parts of yourself, trust your intuition and walk through uncertainty with awareness.`,
  upright: [
    "Intuition – listening to inner wisdom over logic",
    "Dreams – subconscious symbols and messages",
    "Mystery – navigating unknown paths",
    "Shadow work – confronting inner fears and illusions",
    "Sensitivity – emotional depth and psychic openness"
  ],
  reversed: [
    "Confusion – feeling lost or mentally foggy",
    "Deception – being misled or self-deluded",
    "Fear – anxiety amplified by uncertainty",
    "Blocked intuition – ignoring inner truth",
    "Illusion – seeing what you want instead of what is"
  ],
  reading: `When The Moon rises in your reading, it does not offer answers — it offers mirrors. You are being asked to peer through the veil of your life and see what is real, what is projection and what is fear disguised as fact. This card reveals the murkiness of your path, not to frighten you, but to awaken your trust in deeper sight. Not all truths live in daylight.

The Moon represents the emotional tides that pull you inward. It asks you to listen not to noise, but to the still voice within — the one that whispers in dreams and gut feelings. You may feel uncertain, but that’s the nature of inner work. Like moonlight on water, your reflections may shift — but look closer and patterns begin to reveal themselves.

This is your invitation to embrace not-knowing. To move forward even when clarity hasn’t yet arrived. You’re being called to trust your instincts, examine the emotional shadows you avoid and find wisdom in discomfort. The Moon doesn’t lie — it teaches you how to read between the lines of your own soul.

Ask yourself:
- What fears or assumptions might be distorting my perception?
- Am I trusting my intuition, or silencing it with doubt?
- What truths are buried in my dreams, emotions, or discomfort?

The Moon guides not by light, but by inner sight. Follow it inward.`,

  mirroring: ["the-high-priestess-tarot-card-reading-meaning", "the-hanged-man-tarot-card-reading-meaning", "the-devil-tarot-card-reading-meaning"]
},

"the-sun-tarot-card-reading-meaning": {
  slug: "the-sun-tarot-card-reading-meaning",
  name: "The Sun",
  subtitle: "Joy, Clarity, Radiance",
  image: "/card/the-sun-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The Sun brings warmth, celebration and a deep sense of confidence. It is the card of optimism, clarity and vitality. When it shines on your reading, it dissolves shadows and reveals truth, illuminating the path forward with joy and certainty. This card reminds you that your inner light is your greatest strength.`,
  upright: [
    "Joy – a period of happiness and celebration",
    "Clarity – truth is illuminated and confusion fades",
    "Confidence – self-assurance and inner radiance",
    "Vitality – physical wellness and high energy",
    "Success – breakthroughs and visible progress"
  ],
  reversed: [
    "Temporary sadness – joy clouded by doubt or delay",
    "Overconfidence – ego overshadowing clarity",
    "Blocked optimism – struggle to see the light",
    "Impatience – feeling stuck despite progress",
    "False joy – masking wounds with superficial positivity"
  ],
  reading: `The Sun enters your reading like a full breath after long struggle. Its presence is a radiant affirmation: you are seen, supported and standing in your power. This card calls you back to joy — not as escapism, but as sacred clarity. The Sun doesn't merely warm — it reveals. It shows you what’s real, what’s thriving and what you’ve grown beyond.

It invites you to release doubt and let your inner child lead. This is a time for simple pleasures, honest expression and fearless authenticity. You no longer need to dim yourself to fit into spaces that asked for less than your full truth. The Sun urges you to embrace your wholeness and bask in the result of your growth.

Even if you’ve been moving through darkness, this card promises light is returning. Lean into it. You’re not naive for being hopeful — you’re wise for choosing joy when it’s hardest to reach. The Sun reminds you: your life is allowed to feel good.

Ask yourself:
- What truth am I finally ready to embrace, even if it’s uncomfortable?
- Where have I denied myself joy or withheld celebration?
- How can I let my inner light guide me, even through uncertainty?

Let the Sun remind you: you are your own warmth, your own radiance, your own miracle.`,
  mirroring: ["the-empress-tarot-card-reading-meaning", "strength-tarot-card-reading-meaning", "the-fool-tarot-card-reading-meaning"]
},

"judgement-tarot-card-reading-meaning": {
  slug: "judgement-tarot-card-reading-meaning",
  name: "Judgement",
  subtitle: "Awakening, Redemption, Higher Calling",
  image: "/card/judgement-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `Judgement marks a moment of reckoning — where your past, choices and inner truth converge into revelation. It represents an awakening, a call to rise into your higher self. This card invites forgiveness, clarity and courage as you step into a more conscious version of yourself.`,
  upright: [
    "Awakening – spiritual rebirth and deep insight",
    "Redemption – letting go of guilt or self-judgment",
    "Higher calling – being pulled toward your soul’s mission",
    "Reflection – reviewing your past with compassion",
    "Renewal – emerging with new purpose and clarity"
  ],
  reversed: [
    "Self-doubt – fear of rising to your potential",
    "Denial – resisting inner truth or ignoring signs",
    "Regret – fixation on past mistakes",
    "Avoidance – refusing to take accountability",
    "Delayed transformation – clinging to outdated stories"
  ],
  reading: `Judgement is not about punishment — it’s about truth. When this card appears, you are being invited to examine your life not with shame, but with clarity. It’s a cosmic mirror, showing you not only who you’ve been, but who you’re capable of becoming. This is your wake-up call — your soul asking if you’re ready to rise.

The call may feel loud. Uncomfortable. Even terrifying. It may come as a dream, a confrontation, a longing to start over. Whatever form it takes, it’s asking: Are you willing to shed what no longer aligns with your highest self? This card holds sacred space for your rebirth — but only if you choose it.

Now is not the time to look away. It is the time to forgive, release and answer the calling that’s been echoing in your bones. You are more than your past. You are your potential in motion. The gates are open — walk through.

Ask yourself:
- What am I being called to face that I’ve been avoiding?
- Where in my life am I being asked to rise above my past?
- Can I trust that transformation requires truth — even when it hurts?

Judgement doesn’t end a story — it resurrects the soul that’s ready to live it fully.`,
  mirroring: ["death-tarot-card-reading-meaning", "the-lovers-tarot-card-reading-meaning", "the-world-tarot-card-reading-meaning"]
},

"the-world-tarot-card-reading-meaning": {
  slug: "the-world-tarot-card-reading-meaning",
  name: "The World",
  subtitle: "Completion, Wholeness, Integration",
  image: "/card/the-world-tarot-card-reading-meaning.webp",
  suit: "Major Arcana",
  description: `The World signifies fulfillment, wholeness and the completion of a powerful cycle. You have grown, transformed and now arrive at a place of harmony. This card represents the integration of all lessons — a spiritual graduation that prepares you for the next evolution of your journey.`,
  upright: [
    "Completion – the end of a significant chapter",
    "Wholeness – feeling aligned and integrated",
    "Achievement – celebrating hard-earned success",
    "Expansion – stepping into your fullest potential",
    "Unity – harmony between body, mind and spirit"
  ],
  reversed: [
    "Lack of closure – lingering unfinished energy",
    "Stagnation – delaying your next phase",
    "Fragmentation – feeling disconnected or incomplete",
    "Resistance – fear of moving beyond the known",
    "Unrealized potential – hesitating at the threshold"
  ],
  reading: `The World arrives as a sacred breath — the kind that comes after long effort, deep healing, or a soul journey that has fundamentally changed you. This card doesn't just close a chapter — it honors it. Every challenge, breakthrough and quiet resilience has led you here. You are not just ending something. You are completing yourself.

This is a moment to reflect and to celebrate. You may feel a quiet satisfaction or a full-body exhale — both are valid. The World invites you to notice how far you've come, to see how all the pieces fit and to embrace the person you’ve become because of what you’ve endured. Integration is the gift. You no longer carry your past — you embody its wisdom.

Now, the circle closes. But remember: all endings are beginnings in disguise. With this cycle complete, a new one is waiting. Walk forward not as who you were — but as the whole version of who you've become.

Ask yourself:
- What have I finally come to understand or release?
- How can I honor my growth before rushing into the next chapter?
- Am I ready to step into the world with full presence and truth?

The World doesn’t just mark an end — it announces your readiness to begin again, whole and wise.`,
  mirroring: ["the-fool-tarot-card-reading-meaning", "judgement-tarot-card-reading-meaning", "wheel-of-fortune-tarot-card-reading-meaning"]
},

"ace-of-wands-tarot-card-reading-meaning": {
  slug: "ace-of-wands-tarot-card-reading-meaning",
  name: "Ace of Wands",
  subtitle: "Inspiration, Passion, Spark of Action",
  image: "/card/ace-of-wands-tarot-card-reading-meaning.webp",
  suit: "Wands",
  description: `The Ace of Wands is a burst of fire, the first flash of desire, the start of an idea, the seed of action. It symbolizes new beginnings that come from passion and potential. This card urges you to follow what excites you and begin where your spirit feels most alive.`,
  upright: [
    "Inspiration – a powerful idea or calling",
    "Initiation – taking bold action",
    "Creativity – expressing yourself fearlessly",
    "Passion – feeling alive and driven",
    "Opportunity – the spark to start something big"
  ],
  reversed: [
    "Blocked energy – lack of motivation or clarity",
    "Delayed start – fear or hesitation holding you back",
    "Creative block – struggling to express inspiration",
    "Misguided passion – scattered focus or burnout",
    "False starts – enthusiasm without follow-through"
  ],
  reading: `The Ace of Wands doesn’t ask, it ignites. This is the moment before the leap, when you feel the stir of something powerful rising inside you. It is raw, untamed energy, asking to be channeled into form. You may not know the outcome, but you feel the flame, and that is enough to begin.

This card signals the start of something meaningful: a new project, path, or relationship rooted in genuine desire. It urges you to trust the force that pulls you forward, even if it doesn’t make perfect sense yet. Passion is its own kind of wisdom. Listen. Move. Create. Let your fire lead.

Sometimes we wait for the world to give us permission. The Ace of Wands is your permission. Your idea matters. Your desire is sacred. Begin now, not when it's safe, but when it's real.

Ask yourself:
• What excites me enough to risk uncertainty?
• Where have I been waiting instead of creating?
• What would I start if I trusted my inner fire completely?

The Ace of Wands is the match in your hand, all it asks is that you strike it.`,
  mirroring: ["the-magician-tarot-card-reading-meaning", "the-sun-tarot-card-reading-meaning", "two-of-wands-tarot-card-reading-meaning"]
},

"two-of-wands-tarot-card-reading-meaning": {
  slug: "two-of-wands-tarot-card-reading-meaning",
  name: "Two of Wands",
  subtitle: "Vision, Decisions, Exploration",
  image: "/card/two-of-wands-tarot-card-reading-meaning.webp",
  suit: "Wands",
  description: `The Two of Wands stands at the edge of the known world, gazing outward into vast possibility. This card is about vision, about standing in your power and asking what more could be. It speaks to the stage between comfort and action, where choice and courage are your companions.`,
  upright: [
    "Planning – thinking ahead and making long-term decisions",
    "Vision – seeing broader horizons and new paths",
    "Boldness – stepping beyond your comfort zone",
    "Discovery – a hunger for more experience or purpose",
    "Initiation – the beginning of outward expansion"
  ],
  reversed: [
    "Fear of change – hesitating despite the call to grow",
    "Missed opportunities – indecision leads to stagnation",
    "Doubt – lack of faith in your vision or path",
    "Comfort zone – clinging to the familiar over fulfillment",
    "Disconnection – lost between desires and action"
  ],
  reading: `The Two of Wands arrives when your spirit begins to outgrow its current container. It honors what you’ve built, but calls your gaze beyond it. Something within you is stirring, a sense that there is more waiting if only you dare to reach for it. This card isn't about reckless leaps, it is about conscious expansion, calculated vision, and the courage to begin shaping what is next.

In the quiet space between dreaming and doing, the Two of Wands holds a sacred pause. It asks you to reflect deeply: What do you truly want? What lights your soul with a sense of possibility? You may not yet have all the answers, but you have the clarity to start forming them, and that is enough to begin.

Do not wait until everything is certain. Do not shrink your vision to fit your current circumstances. You are being asked to step outside the walls of habit and look toward the horizon of your potential. The world is bigger than your fears. Walk toward it.

Ask yourself:
• Where am I being called to explore beyond the familiar?
• What vision have I suppressed out of fear or practicality?
• Can I begin even if I don’t yet know how the story ends?

Every great journey begins with one bold look outward.`,
  mirroring: ["three-of-wands-tarot-card-reading-meaning", "the-world-tarot-card-reading-meaning", "the-emperor-tarot-card-reading-meaning"]
},

"three-of-wands-tarot-card-reading-meaning": {
  slug: "three-of-wands-tarot-card-reading-meaning",
  name: "Three of Wands",
  subtitle: "Progress, Expansion, Foresight",
  image: "/card/three-of-wands-tarot-card-reading-meaning.webp",
  suit: "Wands",
  description: `The Three of Wands represents momentum. The plans have been made, the first steps taken, and now you stand in the rising glow of potential. This card signifies expansion of dreams, vision, and personal power. It affirms that something is unfolding and that the winds are shifting in your favor.`,
  upright: [
    "Momentum – early progress on long-term goals",
    "Expansion – widening your scope or territory",
    "Opportunity – new pathways and open doors",
    "Preparation – waiting for results of past efforts",
    "Optimism – expecting growth and movement"
  ],
  reversed: [
    "Delays – stalled progress or unforeseen obstacles",
    "Frustration – impatience with slow movement",
    "Doubt – second-guessing your original vision",
    "Setbacks – logistical issues hindering results",
    "Overextension – reaching too far too fast"
  ],
  reading: `The Three of Wands is the pause before the payoff, that sacred space where you have launched your intentions and now await the tide to return. It asks you to stay steady in faith and preparation. Your vision was not in vain, your actions have already set forces into motion. Now is the time to watch the horizon with trust.

You may feel both excitement and anxiety. What will come back to me? Will it be what I hoped for? This card encourages you to hold fast to belief and keep expanding your field of awareness. What you have imagined is making its way toward you, not all at once, but steadily.

It is also a reminder that success is not a finish line, it is a continuum. Keep strategizing, keep preparing for what is next, and remain open to surprising detours that lead to even richer destinations.

Ask yourself:
• Am I prepared to receive what I’ve asked for?
• Where can I make space for more growth and alignment?
• What fear still keeps me anchored to the shore?

The horizon is not the end, it is the beginning of a grander world unfolding.`,
  mirroring: ["two-of-wands-tarot-card-reading-meaning", "wheel-of-fortune-tarot-card-reading-meaning", "six-of-wands-tarot-card-reading-meaning"]
},

"four-of-wands-tarot-card-reading-meaning": {
  slug: "four-of-wands-tarot-card-reading-meaning",
  name: "Four of Wands",
  subtitle: "Celebration, Stability, Milestones",
  image: "/card/four-of-wands-tarot-card-reading-meaning.webp",
  suit: "Wands",
  description: `The Four of Wands is a joyful card of celebration and homecoming. It speaks of reaching a meaningful milestone, one that deserves to be honored. This card embodies community, gratitude, and the sacred act of pausing to appreciate how far you have come.`,
  upright: [
    "Celebration – marking a victory or life event",
    "Harmony – balance in relationships or home",
    "Homecoming – returning to a safe, loved space",
    "Achievement – reaching a major milestone",
    "Unity – connection and shared joy"
  ],
  reversed: [
    "Tension – discord within home or family",
    "Instability – lack of grounding or peace",
    "Unmet expectations – celebrations that feel hollow",
    "Loneliness – feeling disconnected or unacknowledged",
    "Delayed success – postponed joy or milestones"
  ],
  reading: `The Four of Wands reminds you that joy is not just a reward, it is a spiritual necessity. Too often, we rush from one goal to the next, forgetting that the soul needs celebration as much as effort. This card invites you to stop, breathe, and fully receive what you have created.

There may be a sense of reunion, with loved ones, with your values, or with your own sense of belonging. The energy is warm, communal, and grounding. If you have been feeling disconnected, this is a sign to return to your roots and find stability in connection.

Celebrate not because everything is perfect, but because it is meaningful. Let joy be sacred. Let peace be a worthy goal in itself.

Ask yourself:
• What recent victory have I not yet honored?
• Who or what feels like “home” to me — and why?
• How can I create more sacred space for joy?

Gratitude turns every moment into a foundation of light.`,
  mirroring: ["the-sun-tarot-card-reading-meaning", "ten-of-cups-tarot-card-reading-meaning", "six-of-wands-tarot-card-reading-meaning"]
},

"five-of-wands-tarot-card-reading-meaning": {
  slug: "five-of-wands-tarot-card-reading-meaning",
  name: "Five of Wands",
  subtitle: "Conflict, Competition, Tension",
  image: "/card/five-of-wands-tarot-card-reading-meaning.webp",
  suit: "Wands",
  description: `The Five of Wands depicts dynamic tension, ideas clashing, egos colliding, voices rising in chaos. This card suggests inner or outer conflict that stirs discomfort but also sharpens clarity. It is not always a battle to fear; sometimes friction leads to refinement.`,
  upright: [
    "Conflict – interpersonal or internal disagreements",
    "Competition – vying for attention, success, or control",
    "Tension – energy that feels chaotic or scattered",
    "Resistance – struggling to align or cooperate",
    "Testing ground – challenges that sharpen skills"
  ],
  reversed: [
    "Avoidance – suppressing necessary confrontation",
    "Resentment – buried tension erupting unexpectedly",
    "Exhaustion – worn out from conflict or comparison",
    "Submission – giving up your voice to keep peace",
    "Miscommunication – talking over, not to, each other"
  ],
  reading: `The Five of Wands shows up when something inside or around you feels unruly. It could be external, a competitive environment, arguments, misunderstandings, or internal, as your own desires and doubts clash for dominance. Either way, it asks for your presence, not your panic.

This card does not always warn of destruction; it invites engagement. Sometimes we need to wrestle with our limitations or challenge outdated ideas. The goal is not to win, but to discover what truly matters and rise above the noise with clarity.

You cannot always control others’ energy, but you can center your own. Choose your battles wisely, speak your truth without attachment, and seek not dominance, but understanding.

Ask yourself:
• Am I fighting for truth or feeding my ego?
• Where am I afraid of conflict — and why?
• What tension is asking to be expressed honestly?

Chaos can be the fire that forges sharper focus.`,
  mirroring: ["seven-of-wands-tarot-card-reading-meaning", "the-tower-tarot-card-reading-meaning", "five-of-swords-tarot-card-reading-meaning"]
},

"six-of-wands-tarot-card-reading-meaning": {
  slug: "six-of-wands-tarot-card-reading-meaning",
  name: "Six of Wands",
  subtitle: "Victory, Recognition, Achievement",
  image: "/card/six-of-wands-tarot-card-reading-meaning.webp",
  suit: "Wands",
  description: `The Six of Wands heralds a moment of triumph. After effort and endurance, your hard work is seen, respected, and celebrated. This card signals external validation and inner pride, a reminder that it is okay to shine when you have earned it.`,
  upright: [
    "Victory – success after struggle or adversity",
    "Recognition – being praised or acknowledged",
    "Confidence – feeling empowered and capable",
    "Leadership – guiding others through example",
    "Momentum – continued progress and motivation"
  ],
  reversed: [
    "Pride – letting praise inflate ego or entitlement",
    "Imposter syndrome – doubting your own success",
    "Insecurity – fearing loss of approval",
    "Overexposure – discomfort with being in the spotlight",
    "False praise – applause that feels unearned"
  ],
  reading: `The Six of Wands is your standing ovation. It celebrates not just what you have achieved, but how far you have come to get here. You have likely weathered challenges, made sacrifices, and stayed committed when the path felt unclear. Now, others see what you have built, and so should you.

But recognition is not just about applause. It is about reflection. Who did I become in the pursuit of this goal? Did I stay true to my values? Your achievement is not an endpoint, but a mirror, one that reflects both your effort and your evolution.

Receive praise with humility. Accept support without deflecting. Stand tall not from arrogance, but from grounded pride in your journey.

Ask yourself:
• Do I allow myself to feel proud — or do I downplay my wins?
• What does true recognition feel like to me?
• How can I stay humble while still embracing the spotlight?

Victory is not just the finish line, it is the story you tell with your head held high.`,
  mirroring: ["the-chariot-tarot-card-reading-meaning", "four-of-wands-tarot-card-reading-meaning", "ten-of-pentacles-tarot-card-reading-meaning"]
},

"seven-of-wands-tarot-card-reading-meaning": {
  slug: "seven-of-wands-tarot-card-reading-meaning",
  name: "Seven of Wands",
  subtitle: "Defense, Standing Ground, Perseverance",
  image: "/card/seven-of-wands-tarot-card-reading-meaning.webp",
  suit: "Wands",
  description: `The Seven of Wands depicts the moment after you have reached a high point, and now must protect what you have earned. This card represents resilience, asserting your beliefs, and the courage to defend your truth in the face of opposition. It speaks to the battle after success, where strength must be renewed.`,
  upright: [
    "Defending your position – holding your ground with integrity",
    "Courage – standing up against opposition or criticism",
    "Resilience – enduring pressure without giving up",
    "Persistence – refusing to be pushed down or aside",
    "Conviction – believing deeply in your values or path"
  ],
  reversed: [
    "Overwhelm – feeling outnumbered or unsupported",
    "Exhaustion – losing strength in an uphill battle",
    "Avoidance – not wanting to deal with confrontation",
    "Doubt – questioning your right to stand your ground",
    "Withdrawal – giving in or stepping back under pressure"
  ],
  reading: `When the Seven of Wands appears, it reminds you that achievement invites challenge. You have come far, but now the test is not just reaching the summit, it is staying there. Others may question your place, your right, your vision. Do not let the noise shake the roots of your knowing.

This is the card of the warrior heart, the one who stands for what matters, even when it is hard. It speaks to moments where you feel alone in your defense, where you are tired but still upright. Your stance need not be loud or aggressive, it need only be firm. Quiet conviction is power too.

In spiritual terms, this is not just about fending off others, it is about reaffirming your path to yourself. When everything external shakes, return inward. Re-anchor. Your soul chose this journey for a reason. Stand tall in the storm, and let your spirit speak louder than resistance.

Ask yourself:
• What am I being called to defend in my life right now?
• Where am I feeling challenged — and what does that reveal?
• How can I honor my truth without falling into fear or aggression?

You did not climb this far to be pushed off the mountain.`,
  mirroring: ["five-of-wands-tarot-card-reading-meaning", "strength-tarot-card-reading-meaning", "nine-of-wands-tarot-card-reading-meaning"]
},

"eight-of-wands-tarot-card-reading-meaning": {
  slug: "eight-of-wands-tarot-card-reading-meaning",
  name: "Eight of Wands",
  subtitle: "Momentum, Swift Change, Forward Motion",
  image: "/card/eight-of-wands-tarot-card-reading-meaning.webp",
  suit: "Wands",
  description: `The Eight of Wands is a card of rapid movement, communication, and breakthroughs. It signals a phase where energies align and things unfold with speed. What was once stagnant now flows, opening up possibilities and swift resolution.`,
  upright: [
    "Rapid progress – things accelerating quickly",
    "Clear communication – messages and travel",
    "Decisive action – clarity that moves things forward",
    "Alignment – all energies pointing in one direction",
    "Breakthroughs – sudden realizations or green lights"
  ],
  reversed: [
    "Delays – frustration from slowdowns or miscommunication",
    "Scattered energy – too much happening at once",
    "Missed messages – important signals getting lost",
    "Resistance to change – clinging to slow pace",
    "Overwhelm – struggling to keep up with speed"
  ],
  reading: `The Eight of Wands signals the moment after inertia breaks, when things that once crawled now soar. This card does not knock gently. It arrives like a gust of wind and tells you that now is the time. Delays have lifted. The Universe is in motion. So must you be.

It is a cosmic green light, a burst of flow that carries projects, conversations, or dreams forward. Trust that what is coming is swift, aligned, and purposeful. When synchronicities begin to unfold, pay attention. Life is showing you where to aim your energy, and what to let fly.

In spirit, this card is an invitation to ride the current rather than control it. When you let go of needing to plan every step, you make room for magic. Let the arrows fly, and trust they will land exactly where they should.

Ask yourself:
• Where is momentum naturally building in my life right now?
• What needs to move quickly — and what am I still hesitating on?
• How can I align my intentions with the flow already unfolding?

The wind is at your back. Aim true, and leap.`,
  mirroring: ["the-chariot-tarot-card-reading-meaning", "knight-of-wands-tarot-card-reading-meaning", "two-of-wands-tarot-card-reading-meaning"]
},

"nine-of-wands-tarot-card-reading-meaning": {
  slug: "nine-of-wands-tarot-card-reading-meaning",
  name: "Nine of Wands",
  subtitle: "Resilience, Wounded Warrior, Last Stand",
  image: "/card/nine-of-wands-tarot-card-reading-meaning.webp",
  suit: "Wands",
  description: `The Nine of Wands appears when you have endured much, but your spirit still rises. It is the card of perseverance in the face of hardship, a wounded warrior holding the line. You are near the end, but not finished yet.`,
  upright: [
    "Resilience – standing strong despite wounds",
    "Perseverance – continuing even when exhausted",
    "Courage – facing challenges with grit",
    "Boundaries – knowing what is worth protecting",
    "Near completion – almost at the end of a cycle"
  ],
  reversed: [
    "Burnout – pushing yourself past your limit",
    "Defensiveness – building walls too high",
    "Paranoia – fearing the next blow before it comes",
    "Reluctance – not wanting to try again",
    "Wounds unhealed – carrying past pain into the present"
  ],
  reading: `The Nine of Wands does not promise ease, it promises strength. You have fought battles that few have seen, and now you stand, bruised but breathing. This is not weakness, it is spiritual endurance. You have come this far for a reason. You are closer than you think.

Sometimes the soul chooses paths that test every layer of our resilience. This card asks whether you can hold the line for one more breath, one more sunrise. Can you tend your wounds without surrendering your will? The reward lies just beyond this final climb.

Energetically, this card is your reminder that survival is not the endgame, thriving is. You are not meant to live in defense forever. Let this be your last stand, and the first step toward softer ground.

Ask yourself:
• What in me has been tested the most — and how have I endured?
• What am I defending, and is it still worth it?
• How can I move forward with strength without losing softness?

This is the final test before the dawn. Do not give up, rise once more.`,
  mirroring: ["seven-of-wands-tarot-card-reading-meaning", "ten-of-wands-tarot-card-reading-meaning", "strength-tarot-card-reading-meaning"]
},

"ten-of-wands-tarot-card-reading-meaning": {
  slug: "ten-of-wands-tarot-card-reading-meaning",
  name: "Ten of Wands",
  subtitle: "Burden, Overload, Responsibility",
  image: "/card/ten-of-wands-tarot-card-reading-meaning.webp",
  suit: "Wands",
  description: `The Ten of Wands represents carrying too much. It speaks of burdens, some chosen, some inherited, and the weight that builds when you try to hold it all alone. It marks the end of a journey, where pressure peaks before release.`,
  upright: [
    "Heavy burdens – taking on too much responsibility",
    "Completion – final stretch before resolution",
    "Stress – feeling overwhelmed by duties",
    "Sacrifice – carrying the load for others",
    "Hard-earned success – reaching goals through struggle"
  ],
  reversed: [
    "Delegation – asking for support or release",
    "Collapse – buckling under pressure",
    "Obligation – doing things out of guilt or duty",
    "Resentment – frustration at unshared labor",
    "Avoidance – not recognizing the toll it’s taking"
  ],
  reading: `The Ten of Wands is a weighty card, not because you are weak, but because you have taken on too much. Sometimes in our pursuit of success, purpose, or duty, we forget to ask whether we must carry all of this. This card asks you to pause and reevaluate.

You are nearing the finish line, but the weight is unsustainable. Let this card be permission to set some burdens down. Not everything must be carried alone. Ask where your strength has become self sacrifice, and where you have confused martyrdom with meaning.

In soul terms, this is the end of a spiritual cycle. You have grown through labor, but now it is time to lighten the load. Lay down what is not yours. Unburden your soul. Completion is sweeter when you can breathe.

Ask yourself:
• What am I carrying that no longer serves me?
• Who am I trying to save — and at what cost?
• What would it feel like to move through life unburdened?

Let go. What is meant for you will remain, even when your hands are empty.`,
  mirroring: ["the-hermit-tarot-card-reading-meaning", "nine-of-wands-tarot-card-reading-meaning", "ten-of-swords-tarot-card-reading-meaning"]
},

"page-of-wands-tarot-card-reading-meaning": {
  slug: "page-of-wands-tarot-card-reading-meaning",
  name: "Page of Wands",
  subtitle: "Inspiration, Discovery, Enthusiasm",
  image: "/card/page-of-wands-tarot-card-reading-meaning.webp",
  suit: "Wands",
  description: `The Page of Wands bursts with creative energy and unformed potential. This is the card of youthful curiosity, artistic sparks, and a thirst for new adventures. It marks the moment before action, when ideas take root and imagination fuels direction.`,
  upright: [
    "Creative spark – inspiration bubbling to the surface",
    "Curiosity – eagerness to explore or experiment",
    "Excitement – feeling energized by new ideas",
    "Exploration – stepping into new territory",
    "Playfulness – reconnecting with joyful expression"
  ],
  reversed: [
    "Restlessness – starting too many things at once",
    "Immaturity – acting without thinking things through",
    "Blocked creativity – not knowing how to channel inspiration",
    "Fear of failure – hesitation to act on ideas",
    "Scattered focus – too much energy with no grounding"
  ],
  reading: `The Page of Wands is the messenger of creative fire. When it appears, something within you is ready to be born, a project, a calling, a version of yourself you have only just begun to imagine. The spark is real. Now, you must choose to fan it.

This card invites you to play, to dream, to explore without attachment to outcome. It does not need a full plan, it needs your enthusiasm. Often, the Page of Wands shows up when your soul is craving joy and movement. Let yourself follow the pull of excitement.

Spiritually, this is about igniting the inner artist, the dreamer, the wanderer. You are allowed to begin, even if it is messy. What matters is that you listen to what is trying to rise. Give your fire permission to burn.

Ask yourself:
• What new desire or idea has been nudging me lately?
• What’s stopping me from taking the first step?
• How can I let curiosity lead, without pressure to be perfect?

Your fire is rising. Let it guide you toward discovery.`,
  mirroring: ["the-fool-tarot-card-reading-meaning", "knight-of-wands-tarot-card-reading-meaning", "ace-of-wands-tarot-card-reading-meaning"]
},

"knight-of-wands-tarot-card-reading-meaning": {
  slug: "knight-of-wands-tarot-card-reading-meaning",
  name: "Knight of Wands",
  subtitle: "Drive, Passion, Bold Pursuit",
  image: "/card/knight-of-wands-tarot-card-reading-meaning.webp",
  suit: "Wands",
  description: `The Knight of Wands bursts forth with energy, charisma, and ambition. He represents swift movement, bold ideas, and the relentless pursuit of a goal. This card reflects action without delay, a call to chase inspiration, break free of hesitation, and lead with fire.`,
  upright: [
    "Ambition – charging toward your goal with confidence",
    "Boldness – taking initiative and breaking new ground",
    "Adventure – embracing risk and excitement",
    "Momentum – fast-moving developments or travel",
    "Charisma – inspiring others with your presence and ideas"
  ],
  reversed: [
    "Impulsiveness – acting before thinking things through",
    "Arrogance – ignoring guidance or warning signs",
    "Burnout – rushing in without rest or reflection",
    "Recklessness – making hasty or risky decisions",
    "Disruption – plans derailed due to impatience"
  ],
  reading: `The Knight of Wands arrives like a gust of wildfire, hot, intense, and impossible to ignore. He does not knock; he kicks the door open. His presence in a reading signals a moment of intense energy, a push forward, a rising call to act before the spark fades.

Yet passion must be wielded with purpose. The Knight invites you to harness your momentum wisely. Not every path needs to be charged into, but the one that is calling your soul, that is where your fire belongs. This is the season of motion, of not waiting for permission, of letting your heartbeat set the rhythm.

Spiritually, this card dares you to trust your drive and channel it. Ride your passion like a sacred steed, but do not forget to guide the reins. You are not here to burn out. You are here to blaze trails, and light up the path for others who follow.

Ask yourself:
• What pursuit sets my heart ablaze right now?
• Am I charging forward with direction — or just with desire?
• How can I keep my inner fire burning without losing control?

You are the flame in motion, direct it with purpose, and the world moves with you.`,
  mirroring: ["the-chariot-tarot-card-reading-meaning", "ace-of-wands-tarot-card-reading-meaning", "page-of-wands-tarot-card-reading-meaning"]
},

"queen-of-wands-tarot-card-reading-meaning": {
  slug: "queen-of-wands-tarot-card-reading-meaning",
  name: "Queen of Wands",
  subtitle: "Confidence, Radiance, Inner Flame",
  image: "/card/queen-of-wands-tarot-card-reading-meaning.webp",
  suit: "Wands",
  description: `The Queen of Wands radiates magnetic energy, self assurance, and independence. She is the embodiment of fire in its most graceful and assertive form, a natural leader who knows her worth and walks with passion, creativity, and warmth.`,
  upright: [
    "Confidence – embracing your power and magnetism",
    "Charisma – inspiring and drawing others in with ease",
    "Courage – standing boldly in your truth",
    "Leadership – guiding through intuition and integrity",
    "Creativity – channeling fire into meaningful expression"
  ],
  reversed: [
    "Insecurity – doubting your light or suppressing it",
    "Jealousy – comparing yourself to others unfairly",
    "Manipulation – using charm for control",
    "Overextension – giving too much and burning out",
    "Hiddenness – shrinking away from your potential"
  ],
  reading: `The Queen of Wands does not ask for attention, she naturally commands it. When she appears, it is a sign that your inner fire is meant to be seen. This is not arrogance; this is presence. You are being reminded that confidence is not about noise, it is about knowing who you are without apology.

She encourages you to own your radiance. Let your gifts be visible. Step into roles where your spirit can lead, heal, or inspire. You do not have to dim your light for anyone, the right people will be warmed by it, not blinded. This is your moment to reclaim your magic and let your intuition guide bold, beautiful steps.

Spiritually, the Queen is a reminder that power can be tender. That fire can be healing. And that your authenticity is your greatest strength. She speaks to the sacred feminine fire within you, the one that builds, nurtures, protects, and blazes on even through adversity.

Ask yourself:
• Where am I hiding my gifts, and why?
• How can I lead without losing my softness?
• What would it look like to live fully, fiercely, and freely?

You are not too much, you are fire in balance, meant to be seen, felt, and followed.`,
  mirroring: ["the-empress-tarot-card-reading-meaning", "the-sun-tarot-card-reading-meaning", "king-of-wands-tarot-card-reading-meaning"]
},

"king-of-wands-tarot-card-reading-meaning": {
  slug: "king-of-wands-tarot-card-reading-meaning",
  name: "King of Wands",
  subtitle: "Vision, Authority, Mastery of Fire",
  image: "/card/king-of-wands-tarot-card-reading-meaning.webp",
  suit: "Wands",
  description: `The King of Wands is the visionary leader, bold, ambitious, and deeply aligned with purpose. He represents someone who leads with heart and action, not just words. This card signals mastery over creative forces, and the ability to inspire, delegate, and actualize grand visions.`,
  upright: [
    "Leadership – taking charge with clarity and confidence",
    "Vision – seeing the bigger picture and committing to it",
    "Influence – motivating others with passion and presence",
    "Courage – embracing challenge with steadiness",
    "Purpose – living and acting from your highest calling"
  ],
  reversed: [
    "Domineering – overpowering others instead of inspiring",
    "Impatience – rushing plans without clarity",
    "Ego – putting image above integrity",
    "Stubbornness – refusing collaboration or advice",
    "Disconnection – losing touch with deeper purpose"
  ],
  reading: `The King of Wands appears when you are ready to step into full embodiment of your creative power. This is not the reckless energy of the Knight, nor the radiant presence of the Queen, this is direction, vision, and conviction rolled into form. You are being asked to lead, but not just for yourself, for others who depend on your clarity.

He calls on you to stop hesitating and start acting. To build. To delegate. To commit. Your gifts are no longer in the raw, they are refined, and now it is time to leave legacy instead of just spark. This is the moment where leadership becomes spiritual, not just strategy, but soul.

And yet, true power is humble. The King teaches that impact does not come from control, but from alignment. Lead from your why, not your need to prove. Inspire from purpose, not pressure. Your path is not about domination, it is about ignition.

Ask yourself:
• What am I meant to lead right now, and why?
• Am I moving from ego or from essence?
• How can I build something that serves both myself and others?

You are the fire on the throne, strong, steady, sacred. Lead with light.`,
  mirroring: ["the-emperor-tarot-card-reading-meaning", "the-magician-tarot-card-reading-meaning", "queen-of-wands-tarot-card-reading-meaning"]
},



"ace-of-cups-tarot-card-reading-meaning": {
  slug: "ace-of-cups-tarot-card-reading-meaning",
  name: "Ace of Cups",
  subtitle: "Emotional Renewal, Love, Overflowing Spirit",
  image: "/card/ace-of-cups-tarot-card-reading-meaning.webp",
  suit: "Cups",
  description: `The Ace of Cups represents the opening of the heart, the overflowing of emotion and the divine invitation to love and be loved. It symbolizes the birth of compassion, spiritual connection and deep emotional healing. This card marks a moment where joy wells up from within, often unbidden — a new relationship, a spark of inner peace, or a profound spiritual insight.`,
  upright: [
    "New love – romantic, platonic, or self-love blossoming",
    "Emotional healing – releasing pain to make room for joy",
    "Spiritual awakening – connection to divine love and intuition",
    "Compassion – a heart open to give and receive freely",
    "Creative flow – inspiration born from emotional truth"
  ],
  reversed: [
    "Blocked emotions – difficulty expressing or receiving love",
    "Emptiness – feeling numb or emotionally drained",
    "Overwhelm – drowning in unchecked feelings",
    "Unrequited love – offering what is not returned",
    "Closed heart – past wounds inhibiting connection"
  ],
  reading: `When the Ace of Cups flows into your reading, it is a sacred blessing — a divine invitation to open your heart and let it be filled. This is the wellspring of love, compassion and creative emotion rising to meet you at the surface of your life. It doesn’t need to be chased. It arrives like rain on thirsty ground.

This card asks: what in your life is ready to receive love? Whether it’s the start of a relationship, a healing within, or the simple choice to be gentler with yourself, the Ace of Cups reminds you that every drop of compassion given is returned tenfold.

Let this be your emotional beginning. Don’t be afraid to feel fully — to cry, to laugh, to create from your heart. This is not weakness. This is where your strength is reborn. A new chapter begins — one written not from the mind, but from the soul.

Ask yourself:
- Where am I being invited to open my heart without fear?
- What is longing to be expressed emotionally, that I’ve kept bottled up?
- How can I let love — divine, personal, or creative — flow through me today?

When the cup overflows, do not resist it. Drink deeply. Let love be your guide.`,

  mirroring: ["two-of-cups-tarot-card-reading-meaning", "the-star-tarot-card-reading-meaning", "queen-of-cups-tarot-card-reading-meaning"]
},

"two-of-cups-tarot-card-reading-meaning": {
  slug: "two-of-cups-tarot-card-reading-meaning",
  name: "Two of Cups",
  subtitle: "Union, Mutual Love, Soulful Connection",
  image: "/card/two-of-cups-tarot-card-reading-meaning.webp",
  suit: "Cups",
  description: `The Two of Cups represents harmony between souls — a bond forged in trust, respect and emotional balance. Whether romantic, platonic, or spiritual, this card speaks to a sacred exchange of love and energy. It signals the coming together of equals, the beginning of a partnership based on authentic understanding.`,
  upright: [
    "Mutual love – a relationship built on shared emotion and respect",
    "Reconciliation – healing and reunion after distance or hurt",
    "Soul connection – feeling truly seen and met by another",
    "Emotional balance – harmony within and between hearts",
    "Beginning of a sacred union – romantic or spiritual"
  ],
  reversed: [
    "Imbalance – one-sided relationships or unmet needs",
    "Misunderstanding – emotional disconnect or lack of communication",
    "Disunity – growing apart or inner conflict within a bond",
    "Emotional codependency – losing self in another",
    "Unreciprocated feelings – love not returned equally"
  ],
  reading: `When the Two of Cups enters your reading, it calls your heart to attention. This is not about fleeting attraction or shallow companionship — it is about meeting another with clarity, truth and vulnerability. It signifies a mirror held between two souls, reflecting mutual care and emotional resonance.

This card may herald the beginning of a beautiful relationship, or the deepening of an existing bond. But more than anything, it asks: do you honor yourself enough to engage in true partnership? For such a union cannot bloom without self-love first. Your capacity to connect with another depends on the foundation you’ve built within.

To truly receive another, you must be open. To truly give, you must be whole. The Two of Cups whispers that sacred partnership begins not with perfection, but with honesty — and the willingness to grow together.

Ask yourself:
- Am I showing up fully and honestly in my relationships?
- Is this bond a mirror of mutual love — or am I pouring into an empty cup?
- How do I honor the balance between love for others and love for myself?

When hearts meet in truth, miracles unfold. Let love rise and be met in kind.`,

  mirroring: ["ace-of-cups-tarot-card-reading-meaning", "the-lovers-tarot-card-reading-meaning", "ten-of-cups-tarot-card-reading-meaning"]
},

"three-of-cups-tarot-card-reading-meaning": {
  slug: "three-of-cups-tarot-card-reading-meaning",
  name: "Three of Cups",
  subtitle: "Celebration, Sisterhood, Shared Joy",
  image: "/card/three-of-cups-tarot-card-reading-meaning.webp",
  suit: "Cups",
  description: `The Three of Cups is a toast to togetherness. It represents joyous union, community and emotional uplift shared among kindred spirits. This card invites moments of laughter, shared stories, healing conversations and spiritual kinship. It celebrates the power of connection to lift our hearts.`,
  upright: [
    "Celebration – weddings, births, reunions, or joyous milestones",
    "Friendship – meaningful emotional support from your circle",
    "Community – shared emotional healing through others",
    "Harmony – emotional alignment with those around you",
    "Spiritual sisterhood – feminine or sacred group energy"
  ],
  reversed: [
    "Disconnection – feeling out of place within your community",
    "Overindulgence – escapism through pleasure or distraction",
    "Gossip – miscommunication or harmful speech among friends",
    "Isolation – longing for connection but feeling unseen",
    "Fake joy – smiling on the outside, aching within"
  ],
  reading: `The Three of Cups arrives as a warm embrace from your soul tribe — those who see you, uplift you and remind you of your joy. It signals a time to celebrate, not because life is perfect, but because connection itself is reason enough. In togetherness, healing accelerates. In joy, sorrow dissolves.

You are being asked to soften into support, to allow celebration without guilt. Life has rhythms — some for work, others for dance. When this card appears, lean into joy. Let music carry you, let laughter be your medicine. There is healing in the clink of cups, in the echo of stories, in being reminded that you're not alone.

And perhaps, most importantly, this card reminds you to make space for joy without needing justification. You are worthy of celebration — not just after success, but in the sacred now.

Ask yourself:
- When was the last time I celebrated simply being alive?
- Who are the people that uplift me and how can I connect with them more deeply?
- Can I receive joy as a healing practice, not just a reward?

Raise your cup. You are not alone. You are seen, loved and surrounded by light.`,

  mirroring: ["four-of-wands-tarot-card-reading-meaning", "ten-of-cups-tarot-card-reading-meaning", "the-sun-tarot-card-reading-meaning"]
},

"four-of-cups-tarot-card-reading-meaning": {
  slug: "four-of-cups-tarot-card-reading-meaning",
  name: "Four of Cups",
  subtitle: "Discontent, Contemplation, Emotional Withdrawal",
  image: "/card/four-of-cups-tarot-card-reading-meaning.webp",
  suit: "Cups",
  description: `The Four of Cups captures a moment of emotional stagnation — when the heart turns inward, questioning everything it once desired. You may be surrounded by offerings, yet none feel right. This card signals a need to reflect, but warns against becoming so introspective that you miss what’s being offered.`,
  upright: [
    "Reflection – taking emotional inventory before the next step",
    "Pause – retreat to examine desires and dissatisfaction",
    "Missed opportunities – ignoring gifts due to preoccupation",
    "Emotional fatigue – nothing feels satisfying or joyful",
    "Yearning – a quiet longing for something deeper"
  ],
  reversed: [
    "Renewal – reawakening emotional awareness and receptivity",
    "New perspective – seeing blessings that were once overlooked",
    "Emotional opening – readiness to connect again",
    "Release – letting go of cynicism and emotional numbness",
    "Seizing chances – embracing a new offer with courage"
  ],
  reading: `The Four of Cups appears when your heart feels tired — not necessarily broken, but uninspired. You're being called to pause and assess what truly fulfills you. Are your current desires even yours, or have they been shaped by others' expectations? This is the soul’s quiet rebellion against false pursuits.

But be mindful: what you long for may already be in reach, just unseen through your current lens. Sometimes the divine places gifts before us that don't look like what we expected. This card asks you to open not just your eyes, but your heart.

It's okay to retreat — but not forever. The world is still full of wonder. Look again. Something sacred might be waiting to be received.

Ask yourself:
- Am I pushing away love or opportunity out of fear or disappointment?
- What parts of me are asking for rest and renewal?
- Can I soften my grip on what I thought I wanted, to receive what I truly need?

The heart doesn’t always need more — sometimes it needs to feel again.`,

  mirroring: ["five-of-cups-tarot-card-reading-meaning", "the-hermit-tarot-card-reading-meaning", "the-star-tarot-card-reading-meaning"]
},

"five-of-cups-tarot-card-reading-meaning": {
  slug: "five-of-cups-tarot-card-reading-meaning",
  name: "Five of Cups",
  subtitle: "Grief, Disappointment, Emotional Loss",
  image: "/card/five-of-cups-tarot-card-reading-meaning.webp",
  suit: "Cups",
  description: `The Five of Cups is the aching heart, the sting of loss, the heaviness of regret. This card honors sorrow — it doesn't bypass pain but calls you to sit with it. Yet in every depiction of the Five of Cups, not all is lost. Behind the mourning figure are cups still standing, a quiet symbol of hope.`,
  upright: [
    "Grief – loss of love, dreams, or expectations",
    "Disappointment – feeling let down by people or outcomes",
    "Regret – replaying past mistakes or missed chances",
    "Emotional focus – dwelling on what went wrong",
    "Sorrow – needing to process emotional pain fully"
  ],
  reversed: [
    "Acceptance – coming to peace with what cannot be changed",
    "Healing – beginning to move forward after grief",
    "Forgiveness – releasing blame toward self or others",
    "Hope – recognizing what still remains and matters",
    "Emotional growth – integrating pain into wisdom"
  ],
  reading: `The Five of Cups does not ask you to cheer up — it asks you to honor what hurts. Sometimes things don’t work out. Sometimes people leave. Sometimes we lose versions of ourselves we thought would stay forever. This card is an invitation to grieve consciously — not to stay stuck in sorrow, but to let it move through you.

You are allowed to feel the sting. But don’t forget to turn around. Behind you stand the remaining cups — love that endures, lessons gained and the quiet resilience of your soul. Healing begins not when pain vanishes, but when you choose to face it without shame.

This card reminds you that your story doesn’t end in loss. What has spilled is not all there is. New waters flow again — but only when you are ready to look up.

Ask yourself:
- What am I still grieving that I haven’t fully allowed myself to feel?
- Where am I focusing so much on loss that I miss what still supports me?
- Can I forgive myself for what I didn’t know back then?

Let your sorrow speak — and when it's done, let your healing rise.`,

  mirroring: ["three-of-swords-tarot-card-reading-meaning", "ten-of-swords-tarot-card-reading-meaning", "the-star-tarot-card-reading-meaning"]
},
"six-of-cups-tarot-card-reading-meaning": {
  slug: "six-of-cups-tarot-card-reading-meaning",
  name: "Six of Cups",
  subtitle: "Nostalgia, Innocence, Emotional Roots",
  image: "/card/six-of-cups-tarot-card-reading-meaning.webp",
  suit: "Cups",
  description: `The Six of Cups invites you to revisit the emotional landscapes of your past — not to dwell, but to reclaim joy, innocence and the truths of your inner child. It speaks of familiar comforts, kind gestures and healing through memory. This card is a soft return to what made your heart bloom before life became complicated.`,
  upright: [
    "Nostalgia – reflecting on joyful past experiences",
    "Innocence – childlike wonder and trust",
    "Kindness – emotional generosity and affection",
    "Reconnection – healing through old relationships or memories",
    "Harmony – emotional peace through familiarity"
  ],
  reversed: [
    "Stuck in the past – idealizing what was and avoiding the present",
    "Emotional immaturity – reacting through childlike patterns",
    "Disillusionment – old memories no longer bring comfort",
    "Unresolved childhood wounds – surfacing to be healed",
    "Clinging – holding onto something that has already changed"
  ],
  reading: `When the Six of Cups appears, it brings with it a scent of home, a childhood laugh, a photograph tucked in a drawer. It is a gentle pull from your past — a reminder that not everything old must be left behind. Some parts of who you were are still sacred and worthy of bringing forward.

This card may be asking you to return emotionally to a place of softness — where your trust wasn't broken yet, where joy came easily, where your heart knew what it wanted without fear. It might signal reconnection with an old friend, a family member, or even a version of yourself you thought you'd outgrown but now realize you still need.

Yet while this card reveres memory, it also warns against being lost in it. Let nostalgia be a visitor, not a home. Use what was to heal what is, but don’t forget to keep walking forward.

Ask yourself:
- What aspect of my past is calling me with love — not pain?
- Am I idealizing something that no longer serves me?
- How can I re-integrate the wonder and joy of my childhood into my present?

Not all returns are regressions. Some are sacred homecomings.`,

  mirroring: ["the-sun-tarot-card-reading-meaning", "four-of-wands-tarot-card-reading-meaning", "page-of-cups-tarot-card-reading-meaning"]
},

"seven-of-cups-tarot-card-reading-meaning": {
  slug: "seven-of-cups-tarot-card-reading-meaning",
  name: "Seven of Cups",
  subtitle: "Choices, Illusions, Dreams",
  image: "/card/seven-of-cups-tarot-card-reading-meaning.webp",
  suit: "Cups",
  description: `The Seven of Cups represents the realm of dreams, possibilities and distractions. It reveals a time when your imagination is overflowing, but your focus may be scattered. With so many options laid before you, it’s easy to get lost in fantasy or seduced by the allure of what could be, rather than anchoring yourself in reality. This card reminds you that while dreaming is beautiful, discernment is essential.`,
  upright: [
    "Many opportunities – a time of abundant choices",
    "Imagination – rich inner visions and fantasies",
    "Temptation – being drawn to illusions or false promises",
    "Indecision – feeling overwhelmed by options",
    "Desire – longing for ideal outcomes or escape"
  ],
  reversed: [
    "Clarity – seeing through illusion and narrowing focus",
    "Avoidance – refusal to make necessary decisions",
    "Disillusionment – realizing some dreams were unrealistic",
    "Distraction – losing focus on what truly matters",
    "Emotional overwhelm – struggling to ground oneself"
  ],
  reading: `When the Seven of Cups appears, you are standing in the garden of possibility, surrounded by glittering visions that call your name. Each cup holds a different path, a different version of who you could become — yet not all are what they seem. Some are filled with truth, others with illusion. And the challenge lies not in choosing, but in choosing wisely.

This card arrives when your heart is yearning, when your mind is flooded with dreams, or when life offers more doors than you know how to open. It’s a magical moment, but also one that calls for presence. Let your intuition be the lens through which you see clearly. Do not let fantasy seduce you into paralysis or false hope.

Ground yourself. Breathe. Then ask: which of these paths aligns not only with your desires, but with your deepest truth? Sometimes the most enchanting choice is not the most enriching one. Discern with your soul, not your fear.

Ask yourself:
- What am I chasing that might be rooted in illusion rather than purpose?
- Which option reflects my true values, not just my current desires?
- Am I afraid to commit because I fear choosing wrongly?

When the Seven of Cups appears, you are being reminded that your dreams are sacred — but they must be chosen, not chased blindly.`,
  mirroring: ["the-moon-tarot-card-reading-meaning", "the-devil-tarot-card-reading-meaning", "two-of-swords-tarot-card-reading-meaning"]
},

"eight-of-cups-tarot-card-reading-meaning": {
  slug: "eight-of-cups-tarot-card-reading-meaning",
  name: "Eight of Cups",
  subtitle: "Departure, Transition, Emotional Growth",
  image: "/card/eight-of-cups-tarot-card-reading-meaning.webp",
  suit: "Cups",
  description: `The Eight of Cups symbolizes a conscious turning away — not out of failure, but from inner wisdom. You are walking away from something that once mattered, yet no longer nourishes your spirit. This card signals the bravery it takes to leave behind comfort, relationships, or situations that no longer align with your soul’s evolution.`,
  upright: [
    "Letting go – choosing growth over comfort",
    "Spiritual quest – seeking deeper fulfillment",
    "Turning inward – prioritizing emotional truth",
    "Moving on – leaving behind what’s familiar",
    "Re-evaluation – recognizing when it’s time to walk away"
  ],
  reversed: [
    "Avoidance – staying in a situation out of fear",
    "Unfinished business – emotional loose ends",
    "Stagnation – resisting needed change",
    "Fear of solitude – clinging to connections for security",
    "False hope – hoping something will change that won’t"
  ],
  reading: `When the Eight of Cups appears, it is a quiet revolution. A soul decision. You may not have all the answers, but you do have a deep knowing that something must shift. This is not an escape — it is a passage toward truth, even if the path is lonely or unclear. Sometimes growth demands we leave behind what once felt like home.

You are being called to release what is no longer resonant, to honor the wisdom that whispers, "This is not enough for me anymore." Whether it’s a job, a relationship, or a version of yourself, this departure is not abandonment — it is alignment. Trust that what you are walking toward holds greater depth.

There is courage in walking away, even when others don’t understand. Let your journey be sacred. The unknown may feel daunting, but your soul knows where it’s going — even if your mind does not.

Ask yourself:
- What am I holding onto that no longer nourishes my spirit?
- Am I brave enough to choose truth over familiarity?
- What version of myself am I outgrowing?

When the Eight of Cups appears, you are not lost — you are on pilgrimage to something more whole.`,
  mirroring: ["the-hermit-tarot-card-reading-meaning", "death-tarot-card-reading-meaning", "six-of-swords-tarot-card-reading-meaning"]
},

"nine-of-cups-tarot-card-reading-meaning": {
  slug: "nine-of-cups-tarot-card-reading-meaning",
  name: "Nine of Cups",
  subtitle: "Contentment, Gratitude, Fulfillment",
  image: "/card/nine-of-cups-tarot-card-reading-meaning.webp",
  suit: "Cups",
  description: `The Nine of Cups is often called the 'wish card' — a sign of emotional satisfaction and dreams manifesting into reality. It reflects a time when your heart feels full, your desires are being met and joy flows effortlessly. This card invites you to celebrate your blessings and relish the sweetness of the present moment.`,
  upright: [
    "Emotional satisfaction – deep contentment and joy",
    "Gratitude – appreciating what you have",
    "Fulfillment – wishes coming true",
    "Pride – enjoying personal accomplishments",
    "Self-love – feeling whole within oneself"
  ],
  reversed: [
    "Hollow success – getting what you want but still feeling empty",
    "Overindulgence – pleasure without balance",
    "Disconnection – not appreciating your blessings",
    "Self-centeredness – focusing only on personal gain",
    "Unrealistic expectations – wanting more than is sustainable"
  ],
  reading: `When the Nine of Cups appears, it is a gentle exhale — a reminder that you have come far and deserve to feel the joy that blooms from within. This is not about ego or excess, but about honoring the emotional milestones you’ve reached. Let yourself feel proud. Let yourself savor the beauty.

Sometimes we are so focused on what’s next that we forget to sit in the radiance of now. This card asks you to look around and truly see what is good. Let contentment be a ritual. Let gratitude be your heartbeat. Fulfillment is not a destination — it is a practice.

You are being reminded that joy is sacred. Allow yourself to feel it without guilt, to receive it without question. This moment is a gift and you are allowed to unwrap it fully.

Ask yourself:
- Am I allowing myself to feel the joy I’ve earned?
- What do I take for granted that is actually a blessing?
- Can I be at peace with what I have, even as I dream of more?

When the Nine of Cups appears, happiness is not a future goal — it’s the cup you’re already holding.`,
  mirroring: ["the-empress-tarot-card-reading-meaning", "four-of-wands-tarot-card-reading-meaning", "ten-of-pentacles-tarot-card-reading-meaning"]
},

"ten-of-cups-tarot-card-reading-meaning": {
  slug: "ten-of-cups-tarot-card-reading-meaning",
  name: "Ten of Cups",
  subtitle: "Emotional Harmony, Unity, Love Fulfilled",
  image: "/card/ten-of-cups-tarot-card-reading-meaning.webp",
  suit: "Cups",
  description: `The Ten of Cups is the card of emotional wholeness, spiritual connection and shared joy. It represents not only the fulfillment of individual dreams, but the harmonious union of hearts — family, partnership, or community. This card radiates love, peace and a sense of belonging.`,
  upright: [
    "Harmony – deep emotional peace with loved ones",
    "Family – joy in connection and togetherness",
    "Fulfillment – spiritual and emotional contentment",
    "Alignment – living in accordance with your values",
    "Lasting love – stable and supportive relationships"
  ],
  reversed: [
    "Disharmony – conflict within close relationships",
    "False ideals – chasing perfection in love or family",
    "Disconnection – feeling emotionally isolated",
    "Broken expectations – reality not meeting dreams",
    "Longing – yearning for deeper bonds"
  ],
  reading: `When the Ten of Cups appears, it is the soul’s exhale after a long journey. You have found or are moving toward a place of emotional resonance — where love flows freely and peace is no longer a fleeting feeling but a foundation. This card sings of shared joy, of homes built on heart and not just walls.

You are being invited to receive love more deeply — to let yourself be held by those who see you and to pour that same love back into your circles. This is not just personal happiness, but collective harmony. It is about finding or creating emotional sanctuary.

Whether this card reflects current reality or a vision just over the horizon, it reminds you that love is not a myth — it is a mirror. And when you offer love from a place of truth, it will find its way back to you.

Ask yourself:
- What relationships bring me the deepest peace?
- Am I allowing myself to be loved as fully as I love others?
- What does emotional fulfillment look like for me — truly?

When the Ten of Cups appears, know that love is your birthright — and joy, your sacred home.`,
  mirroring: ["the-sun-tarot-card-reading-meaning", "two-of-cups-tarot-card-reading-meaning", "four-of-wands-tarot-card-reading-meaning"]
},

"page-of-cups-tarot-card-reading-meaning": {
  slug: "page-of-cups-tarot-card-reading-meaning",
  name: "Page of Cups",
  subtitle: "Creative Innocence, Emotional Curiosity, Messages of the Heart",
  image: "/card/page-of-cups-tarot-card-reading-meaning.webp",
  suit: "Cups",
  description: `The Page of Cups embodies youthful wonder and the first stirrings of emotional or artistic exploration. This card signals the arrival of a message — often unexpected — that opens your heart to something new. It carries innocence, creative insight and emotional vulnerability, encouraging you to listen to the whispers of your inner child.`,
  upright: [
    "New emotional experiences – openness to love or friendships",
    "Creative spark – beginning of artistic or intuitive expression",
    "Emotional messages – unexpected or sincere news",
    "Playfulness – viewing life with wonder and softness",
    "Dreaminess – connecting with fantasy, poetry and imagination"
  ],
  reversed: [
    "Emotional immaturity – avoiding responsibility or depth",
    "Creative blocks – ignoring your artistic voice",
    "Insecurity – fear of expressing true feelings",
    "Oversensitivity – taking things too personally",
    "Naivety – misinterpreting emotions or signs"
  ],
  reading: `When the Page of Cups appears, it arrives like a soft breeze that stirs the surface of still water. Something gentle is unfolding — a realization, a confession, a creative impulse — asking you not to analyze, but to feel. This card doesn’t shout; it whispers. It opens the door to wonder and invites you to step through with a trusting heart.

You may be receiving an emotional message soon, or discovering a side of yourself that longs to create, love, or simply dream. The Page is the soul's muse — innocent, unfiltered and courageous in its vulnerability. You are being reminded that sensitivity is not weakness, but sacred wisdom.

To embrace this card fully, soften your defenses. Let curiosity guide you. Let yourself be surprised by the sweetness of life, even if just for a moment. Within your heart, there is still a child who believes in magic — and this card tells you it’s safe to listen.

Ask yourself:
- Where in my life am I being asked to lead with innocence and openness?
- What creative or emotional truth have I been afraid to explore?
- Can I receive love or inspiration without overthinking it?

When the Page of Cups appears, know that a tender message is being delivered — not to your mind, but to your heart.`,
  mirroring: ["the-fool-tarot-card-reading-meaning", "the-star-tarot-card-reading-meaning", "two-of-cups-tarot-card-reading-meaning"]
},

"knight-of-cups-tarot-card-reading-meaning": {
  slug: "knight-of-cups-tarot-card-reading-meaning",
  name: "Knight of Cups",
  subtitle: "Romantic Pursuit, Artistic Vision, Emotional Action",
  image: "/card/knight-of-cups-tarot-card-reading-meaning.webp",
  suit: "Cups",
  description: `The Knight of Cups is the poet, the dreamer, the lover who follows his heart. This card signifies a journey or action inspired by emotion or intuition — whether it’s the pursuit of love, the birth of a creative project, or an idealistic vision you are ready to bring into motion. The Knight reminds you that passion is not passive; it moves, quests and believes.`,
  upright: [
    "Romantic pursuit – following your heart toward connection",
    "Artistic drive – taking action on creative dreams",
    "Emotional bravery – expressing your feelings with sincerity",
    "Charm – enchanting energy that draws people in",
    "Idealism – holding onto noble dreams and values"
  ],
  reversed: [
    "Over-romanticizing – falling for fantasy over reality",
    "Moodiness – letting emotions dictate actions",
    "Avoidance – charming your way out of depth",
    "Emotional manipulation – using feelings to control or influence",
    "Creative inconsistency – starting without finishing"
  ],
  reading: `When the Knight of Cups appears, your heart is not content to sit still. There is a longing here — for love, for beauty, for something meaningful to pursue. This card signals the time to act on your emotional desires. It could mean sending that message, starting that poem, or saying yes to something that stirs your soul.

But the Knight also teaches discernment. Passion without grounding can lead you chasing illusions. You must balance your idealism with self-awareness. Are you in love with someone or with the idea of being in love? Are you pursuing your art or the attention it brings? Ask gently, but ask honestly.

At its best, the Knight of Cups invites you into a sacred dance with inspiration. You are the vessel of emotion and vision — now ride forward with integrity, tenderness and truth.

Ask yourself:
- Am I taking meaningful action on my emotional or creative desires?
- Where am I being guided by beauty and where am I being seduced by illusion?
- Can I express what I feel without fear of rejection or misunderstanding?

When the Knight of Cups appears, your heart is your compass — follow it bravely, but keep your eyes open to the road ahead.`,
  mirroring: ["the-lovers-tarot-card-reading-meaning", "page-of-cups-tarot-card-reading-meaning", "the-chariot-tarot-card-reading-meaning"]
},

"queen-of-cups-tarot-card-reading-meaning": {
  slug: "queen-of-cups-tarot-card-reading-meaning",
  name: "Queen of Cups",
  subtitle: "Compassion, Intuition, Emotional Depth",
  image: "/card/queen-of-cups-tarot-card-reading-meaning.webp",
  suit: "Cups",
  description: `The Queen of Cups is the embodiment of emotional maturity, spiritual intuition and nurturing energy. She sits on her throne with a heart open wide, not because she is fragile, but because she is powerful in her ability to feel, hold and heal. This card calls you to embrace your empathic gifts and to trust the sacred wisdom that comes from deep feeling.`,
  upright: [
    "Compassion – offering support and understanding to others",
    "Emotional intelligence – wise and grounded sensitivity",
    "Intuition – strong spiritual and emotional insight",
    "Nurturing – caring for others with love and calm",
    "Healing – emotional comfort and restoration"
  ],
  reversed: [
    "Emotional overwhelm – taking on others’ pain",
    "Codependency – losing yourself in another’s needs",
    "Suppressed emotions – hiding your feelings to keep peace",
    "Moodiness – sensitivity turned inward or erratic",
    "Manipulation – using emotion as a subtle control"
  ],
  reading: `When the Queen of Cups appears, she comes to you as a mirror — not of your surface self, but of your emotional and spiritual depth. She reminds you that tenderness is not weakness, that your feelings are not flaws and that your ability to empathize is sacred. This is a card of healing — within and for others.

You may be called to hold space for someone else, or to hold space for yourself in a way you never have. Trust your intuition. Let your emotions guide your understanding rather than dismissing them as noise. The Queen teaches that deep feeling is a form of divine wisdom.

She sits quietly by the water, not because she fears the tide — but because she knows how to read its rhythm. So too must you listen deeply, feel fully and respond gently. There is magic in your stillness.

Ask yourself:
- Where am I being asked to respond with compassion rather than control?
- How can I honor my sensitivity as a source of strength?
- Am I listening to what my heart truly feels beneath the surface?

When the Queen of Cups appears, know that empathy is your superpower — and love, your sacred language.`,
  mirroring: ["the-high-priestess-tarot-card-reading-meaning", "the-empress-tarot-card-reading-meaning", "four-of-cups-tarot-card-reading-meaning"]
},

"king-of-cups-tarot-card-reading-meaning": {
  slug: "king-of-cups-tarot-card-reading-meaning",
  name: "King of Cups",
  subtitle: "Emotional Mastery, Wisdom, Calm Authority",
  image: "/card/king-of-cups-tarot-card-reading-meaning.webp",
  suit: "Cups",
  description: `The King of Cups represents emotional balance, maturity and the power of calm leadership. He has mastered the stormy seas of the heart and now rules with a steady, compassionate hand. This card signals your ability to lead with wisdom, offer stability in emotional situations and stay centered even when others are in turmoil.`,
  upright: [
    "Emotional maturity – calm under pressure",
    "Compassionate leadership – guiding with empathy",
    "Balance – navigating logic and emotion with grace",
    "Diplomacy – resolving conflict with kindness",
    "Creative mentorship – nurturing others’ growth"
  ],
  reversed: [
    "Emotional detachment – repressing feelings or shutting down",
    "Coldness – withholding empathy or affection",
    "Manipulation – using emotional insight for control",
    "Imbalance – over-identifying with either mind or heart",
    "Instability – being overwhelmed by internal emotions"
  ],
  reading: `When the King of Cups appears, you are being asked to embody emotional maturity — to be the calm within the storm. Whether you’re navigating your own inner waters or supporting someone else through theirs, this card calls for compassionate leadership. Not dominance, but deep emotional presence.

This King doesn't avoid emotion — he understands it deeply. He listens without judgment, speaks with kindness and acts from wisdom. You are being asked to rise into your strength — not through force, but through emotional mastery. Be the anchor others can hold and the guide they can trust.

Let your steadiness be a gift. Even if you are feeling waves inside, the ability to hold yourself with grace is what allows others to find peace in your presence. The King teaches: power is quiet and love is a legacy.

Ask yourself:
- Am I leading with wisdom or reacting from emotion?
- How can I offer support without losing my own center?
- Where in my life can I bring calm to chaos?

When the King of Cups appears, your greatest strength lies not in control — but in compassion, grounded in truth.`,
  mirroring: ["justice-tarot-card-reading-meaning", "temperance-tarot-card-reading-meaning", "queen-of-cups-tarot-card-reading-meaning"]
},

"ace-of-swords-tarot-card-reading-meaning": {
  slug: "ace-of-swords-tarot-card-reading-meaning",
  name: "Ace of Swords",
  subtitle: "Clarity, Breakthroughs, Mental Power",
  image: "/card/ace-of-swords-tarot-card-reading-meaning.webp",
  suit: "Swords",
  description: `The Ace of Swords slices through illusion to reveal the truth beneath. This card is a symbol of sharp intellect, clarity of thought and the power to see things as they are. Like a bolt of lightning, it brings sudden insight or revelation that changes your perception forever. It represents a new beginning in the realm of the mind — a chance to make decisions based on truth and justice rather than emotion or confusion.`,
  upright: [
    "Mental clarity – seeing situations with precision and truth",
    "Breakthroughs – sudden realizations or epiphanies",
    "New ideas – a surge of intellectual or creative energy",
    "Honest communication – direct, clear and powerful expression",
    "Justice – cutting through bias to uphold truth"
  ],
  reversed: [
    "Confusion – mental fog or overwhelming thoughts",
    "Miscommunication – unclear or harmful words",
    "Mental blocks – difficulty making decisions or thinking clearly",
    "Delusion – refusing to see the truth",
    "Harsh judgment – overly critical or cold-minded behavior"
  ],
  reading: `When the Ace of Swords appears in your reading, it arrives like the first breath of cold air after a storm — invigorating, sobering and undeniable. This card does not whisper; it announces. It pierces through the noise, the lies and the chaos to bring a flash of clarity that demands action.

You are being handed a sword of truth — not for battle, but for discernment. It is time to cut away the illusions that cloud your mind and make decisions from a place of empowered understanding. There is power in your voice, integrity in your thoughts and a moment now where truth can reshape your path.

This card invites you to speak boldly, to think freely and to rise into mental sovereignty. Even if the truth is uncomfortable, it is sacred. And it is yours to hold.

Ask yourself:
- What truth have I been avoiding because it is inconvenient or painful?
- Where am I being called to speak up or stand up for what is right?
- Can I trust my mind to lead when my heart feels clouded?

The Ace of Swords reminds you that truth is not always soft — but it is always liberating.`,
  mirroring: ["justice-tarot-card-reading-meaning", "the-tower-tarot-card-reading-meaning", "king-of-swords-tarot-card-reading-meaning"]
},

"two-of-swords-tarot-card-reading-meaning": {
  slug: "two-of-swords-tarot-card-reading-meaning",
  name: "Two of Swords",
  subtitle: "Indecision, Inner Conflict, Impasse",
  image: "/card/two-of-swords-tarot-card-reading-meaning.webp",
  suit: "Swords",
  description: `The Two of Swords presents a moment of stasis — a quiet before action, a pause filled with tension. It depicts a state of inner conflict or avoidance, where two opposing truths or paths weigh equally and no decision feels quite right. This is the card of being stuck in mental limbo, seeking peace through non-action while the world awaits your move.`,
  upright: [
    "Difficult choices – stuck between two opposing options",
    "Avoidance – refusing to face a necessary truth",
    "Inner conflict – battling with self-doubt or duality",
    "Detachment – emotional numbing to avoid pain",
    "Seeking balance – striving for objectivity amidst confusion"
  ],
  reversed: [
    "Mental overload – paralysis through overthinking",
    "Emotional bias – letting feelings cloud clear judgment",
    "Rushed decisions – reacting without full understanding",
    "Denial – ignoring the signs or truths before you",
    "Breakdown – pressure finally forcing action"
  ],
  reading: `The Two of Swords asks you to sit in the discomfort of not knowing — to acknowledge the space between what you feel and what you think. You may be tempted to avoid a decision, hoping the situation resolves itself, but silence does not equal resolution. This card reflects the moment you hold your breath before diving in.

There is wisdom in stillness, but stagnation is not the same as peace. If you've been denying your inner truth or trying to "wait it out," it’s time to look within. What are you afraid to confront? What consequences are you shielding yourself from?

When this card appears, the answer is not always logical — it is intuitive. You must trust the quiet voice beneath the noise, the knowing behind the doubt.

Ask yourself:
- What choice have I been avoiding and why?
- Have I been silencing my own voice to keep the peace?
- What truth would I face if I removed the blindfold?

The Two of Swords reminds you that the pause is sacred — but the choice is yours to make.`,
  mirroring: ["justice-tarot-card-reading-meaning", "the-hanged-man-tarot-card-reading-meaning", "eight-of-swords-tarot-card-reading-meaning"]
},

"three-of-swords-tarot-card-reading-meaning": {
  slug: "three-of-swords-tarot-card-reading-meaning",
  name: "Three of Swords",
  subtitle: "Heartbreak, Grief, Emotional Pain",
  image: "/card/three-of-swords-tarot-card-reading-meaning.webp",
  suit: "Swords",
  description: `Few cards cut as deeply as the Three of Swords. It represents sorrow, betrayal, or the kind of heartbreak that shifts the ground beneath you. This card does not hide from emotional pain — it faces it head-on, acknowledging that grief is part of the human journey. Though it stings, it also clears the way for healing by making space for the truth.`,
  upright: [
    "Heartbreak – deep emotional pain or separation",
    "Grief – mourning a loss or shattered hope",
    "Truth revealed – painful honesty or betrayal",
    "Emotional release – crying, mourning, or opening wounds",
    "Catharsis – clearing the way for new healing"
  ],
  reversed: [
    "Lingering pain – refusing to let go of heartbreak",
    "Suppression – bottling up grief or sorrow",
    "False recovery – pretending you're okay before you're ready",
    "Fear of vulnerability – avoiding connection to prevent pain",
    "Emotional numbness – stuck in despair without expression"
  ],
  reading: `The Three of Swords comes when something has pierced the heart — a sharp truth, a broken connection, a wound that refuses to be ignored. Its arrival may feel cruel, but its purpose is not to punish — it is to awaken. This pain is not meant to define you, but to remind you that you have loved, trusted and felt deeply.

You are being asked not to deny your suffering but to honor it. Let the tears come. Let the sorrow speak. This card asks you to hold your own heart gently, to sit with what has shattered and to offer yourself the grace of time.

Healing begins the moment you stop pretending you are fine. Allow this heartbreak to hollow you out — not to leave you empty, but to make space for something more true.

Ask yourself:
- What am I grieving that I haven’t fully acknowledged?
- Have I given myself permission to truly feel this pain?
- What might grow from the space this sorrow has carved?

The Three of Swords assures you that brokenness is not failure — it is the beginning of becoming whole.`,
  mirroring: ["ten-of-swords-tarot-card-reading-meaning", "five-of-cups-tarot-card-reading-meaning", "the-tower-tarot-card-reading-meaning"]
},

"four-of-swords-tarot-card-reading-meaning": {
  slug: "four-of-swords-tarot-card-reading-meaning",
  name: "Four of Swords",
  subtitle: "Rest, Reflection, Recuperation",
  image: "/card/four-of-swords-tarot-card-reading-meaning.webp",
  suit: "Swords",
  description: `The Four of Swords calls for a sacred pause — a retreat into stillness where the mind can rest and the heart can breathe. After conflict or emotional upheaval, this card signals a period of recovery, a moment to gather strength before moving forward. It is not a card of avoidance, but of necessary restoration.`,
  upright: [
    "Rest – taking a break to recharge mentally and emotionally",
    "Meditation – finding inner peace through quiet reflection",
    "Recovery – healing from illness, heartbreak, or burnout",
    "Solitude – retreating to reconnect with yourself",
    "Perspective – stepping back to gain clarity"
  ],
  reversed: [
    "Restlessness – difficulty slowing down or relaxing",
    "Burnout – pushing yourself beyond your limits",
    "Avoidance – using rest as a way to escape confrontation",
    "Mental chaos – thoughts racing without resolution",
    "Unprocessed pain – needing healing but resisting the pause"
  ],
  reading: `When the Four of Swords appears, it is not an invitation — it is a necessity. Your spirit has been running too long without pause. This is the sacred silence after the storm, the deep exhale that the soul so often forgets to take.

You are being asked to withdraw, not as a sign of weakness but as an act of strength. In this stillness, wisdom surfaces. In this pause, your inner voice grows louder. True clarity often arrives not in action, but in rest.

Give yourself permission to stop — to listen, to breathe and to rebuild your energy. Your healing is not a luxury. It is a birthright.

Ask yourself:
- When did I last allow myself to truly rest?
- Am I avoiding healing by staying busy?
- What message awaits me in the silence?

The Four of Swords reminds you that peace is not a destination — it is a state you must choose to return to.`,
  mirroring: ["the-hermit-tarot-card-reading-meaning", "four-of-cups-tarot-card-reading-meaning", "temperance-tarot-card-reading-meaning"]
},

"five-of-swords-tarot-card-reading-meaning": {
  slug: "five-of-swords-tarot-card-reading-meaning",
  name: "Five of Swords",
  subtitle: "Conflict, Ego Battles, Hollow Victory",
  image: "/card/five-of-swords-tarot-card-reading-meaning.webp",
  suit: "Swords",
  description: `The Five of Swords speaks of conflict — the kind where winning feels empty and pride overshadows peace. It reveals arguments, power struggles, or a moment where you or others may act out of self-interest. This card invites you to look closely at what you're fighting for and whether the cost of victory is truly worth it.`,
  upright: [
    "Conflict – disagreements, arguments, or tension",
    "Ego clashes – battles of will or control",
    "Hollow victory – winning at the cost of others' well-being",
    "Self-interest – prioritizing your gain over harmony",
    "Manipulation – playing mind games or coercion"
  ],
  reversed: [
    "Regret – remorse over past arguments or actions",
    "Resolution – seeking reconciliation or peace",
    "Letting go – walking away from toxic battles",
    "Self-awareness – recognizing patterns of conflict",
    "Avoidance – fearing confrontation or standing your ground"
  ],
  reading: `The Five of Swords appears when the air is thick with words unspoken or blades sharpened by pride. You may have won the argument — but at what cost? This card does not shame you, but it does challenge you to consider your motives.

It asks you to reflect on the difference between being right and being wise. Have you sought to prove your worth by diminishing others? Or allowed someone else’s ego to override your truth? It’s time to examine the battlefield and ask what truly matters now.

Peace is not weakness. Humility is not defeat. Sometimes, the bravest thing is not to win — but to walk away.

Ask yourself:
- Am I clinging to a victory that left me feeling emptier?
- Have I allowed pride or fear to dominate my relationships?
- What would it look like to choose peace instead of power?

The Five of Swords teaches that real strength lives not in domination — but in self-honesty.`,
  mirroring: ["seven-of-wands-tarot-card-reading-meaning", "the-devil-tarot-card-reading-meaning", "six-of-swords-tarot-card-reading-meaning"]
},

"six-of-swords-tarot-card-reading-meaning": {
  slug: "six-of-swords-tarot-card-reading-meaning",
  name: "Six of Swords",
  subtitle: "Transition, Recovery, Letting Go",
  image: "/card/six-of-swords-tarot-card-reading-meaning.webp",
  suit: "Swords",
  description: `The Six of Swords speaks of transition — a passage from turbulence into calm. It represents the moment when one chooses to move forward, leaving behind emotional or mental burdens. Whether it's a literal journey or a metaphorical one, this card is a symbol of hope, healing and forward motion after hardship. The water may still ripple, but a quieter horizon awaits.`,
  upright: [
    "Moving on – choosing peace over conflict",
    "Healing journey – emotional recovery and restoration",
    "New direction – leaving behind what no longer serves",
    "Assisted passage – support during a difficult time",
    "Mental clarity – gaining perspective after turmoil"
  ],
  reversed: [
    "Resistance to change – clinging to the past",
    "Stagnation – stuck in emotional patterns",
    "Delayed transitions – interrupted progress",
    "Unresolved baggage – avoiding emotional processing",
    "False start – trying to move forward without healing"
  ],
  reading: `When the Six of Swords appears, it is the quiet voice urging you toward calmer waters. You may be emerging from a difficult chapter — perhaps one marked by conflict, grief, or confusion — and this card signals that a turning point is near. It does not promise a complete resolution, but rather a moment of grace, a lull that allows space to breathe again.

This card honors the struggle you’ve endured and validates your need for rest and reprieve. It suggests that healing does not require you to forget — only to move with your pain instead of against it. You are not being asked to run, but to drift forward gently with trust that the current carries you toward peace. The destination may not be clear, but the momentum is real.

You are not alone on this journey. Whether supported by loved ones, spirit, or your inner strength, the crossing has already begun. Let this be a moment where your heart softens, your mind settles and your soul begins to mend.

Ask yourself:
- What am I still carrying that I’m ready to set down?
- Who or what is helping me through this transition — and am I allowing myself to receive it?
- Am I willing to trust the direction my healing is taking me?

The Six of Swords is a whisper that you’re already on the way to somewhere lighter — don’t turn back now.`,
  mirroring: ["the-moon-tarot-card-reading-meaning", "eight-of-cups-tarot-card-reading-meaning", "three-of-swords-tarot-card-reading-meaning"]
},

"seven-of-swords-tarot-card-reading-meaning": {
  slug: "seven-of-swords-tarot-card-reading-meaning",
  name: "Seven of Swords",
  subtitle: "Strategy, Deception, Self-Preservation",
  image: "/card/seven-of-swords-tarot-card-reading-meaning.webp",
  suit: "Swords",
  description: `The Seven of Swords is a card of cunning, secrecy and clever maneuvering. It highlights the tension between honesty and survival — the moments when one must act behind the scenes or rethink how truth is wielded. This card can suggest either deceit or a need for discretion. It reminds us that sometimes strategy is necessary, but it must be aligned with integrity.`,
  upright: [
    "Strategy – planning carefully before acting",
    "Tact – handling a situation discreetly",
    "Self-preservation – protecting your energy or truth",
    "Creative problem solving – thinking outside the box",
    "Independence – going it alone for your own good"
  ],
  reversed: [
    "Deceit – being dishonest with yourself or others",
    "Guilt – struggling with hidden actions or motives",
    "Exposure – secrets coming to light",
    "Sabotage – undermining your own progress",
    "Avoidance – escaping responsibility"
  ],
  reading: `When the Seven of Swords enters your spread, it asks: what am I not saying — and why? This card can point to moments when you’ve needed to protect yourself by staying quiet, or times when you’ve bent the truth in ways that felt justified. It doesn't judge — but it does invite scrutiny.

Sometimes we hide because we fear vulnerability or betrayal. Sometimes we strategize because the path ahead requires careful navigation. This card urges you to examine your motives: are you acting in alignment with your values, or hiding from your truth? The answer determines whether your silence empowers or erodes you.

This is a chance to reclaim honesty as a sacred weapon — one that serves you, not shames you. The true question is not whether you can get away with something, but whether you can live peacefully with how you’ve chosen to move.

Ask yourself:
- Am I withholding something important out of fear or wisdom?
- What truth am I avoiding that would set me free if I faced it?
- Have my silent decisions built safety — or distance?

The Seven of Swords is a mirror — not of deception, but of intention. Hold it close and choose your next step with conscious clarity.`,
  mirroring: ["the-high-priestess-tarot-card-reading-meaning", "five-of-swords-tarot-card-reading-meaning", "justice-tarot-card-reading-meaning"]
},

"eight-of-swords-tarot-card-reading-meaning": {
  slug: "eight-of-swords-tarot-card-reading-meaning",
  name: "Eight of Swords",
  subtitle: "Restriction, Limiting Beliefs, Mental Trap",
  image: "/card/eight-of-swords-tarot-card-reading-meaning.webp",
  suit: "Swords",
  description: `The Eight of Swords reveals a state of mental imprisonment — where fear, doubt, or shame distort clarity. Though the binds appear tight, they are often self-imposed. This card reflects times when you feel stuck or powerless, not because of real limitations, but because of the stories you’ve come to believe.`,
  upright: [
    "Feeling trapped – stuck in a mental loop",
    "Self-imposed limits – internalizing false beliefs",
    "Fear – letting anxiety control decisions",
    "Helplessness – loss of personal agency",
    "Avoidance – unwillingness to confront truth"
  ],
  reversed: [
    "Breakthrough – seeing the truth beyond fear",
    "Liberation – unbinding yourself from lies",
    "Self-empowerment – reclaiming choice",
    "Clarity – dissolving illusion",
    "New mindset – shifting the internal narrative"
  ],
  reading: `The Eight of Swords may arrive when your thoughts have become your cage. You might feel paralyzed by overthinking, caught in an emotional loop, or trapped by a fear that keeps you from moving forward. But this card gently reminds you: the walls are made of paper. The key is yours.

You are not powerless. Even if external forces have contributed to your feeling stuck, the path to freedom begins within. This is a call to name the fear, to challenge the belief and to recognize that your perception is not the full truth. There is more waiting for you outside this narrow view.

Liberation doesn’t require immediate answers. It begins with a whisper: "What if I'm not actually stuck?" Let this be the first thread you pull to unravel the illusion. You have not lost your way — just your faith in it.

Ask yourself:
- What thought or belief has kept me frozen in place?
- Am I willing to question the voice that tells me I can’t?
- What would freedom feel like — and do I believe I deserve it?

The Eight of Swords is not the end — it is the threshold of your liberation. The blindfold is ready to fall.`,
  mirroring: ["the-devil-tarot-card-reading-meaning", "nine-of-swords-tarot-card-reading-meaning", "the-moon-tarot-card-reading-meaning"]
},

"nine-of-swords-tarot-card-reading-meaning": {
  slug: "nine-of-swords-tarot-card-reading-meaning",
  name: "Nine of Swords",
  subtitle: "Anxiety, Nightmares, Inner Demons",
  image: "/card/nine-of-swords-tarot-card-reading-meaning.webp",
  suit: "Swords",
  description: `The Nine of Swords reflects the deep corners of the mind where worry multiplies. It speaks to sleepless nights, emotional spirals and fears that haunt in silence. This card doesn’t predict suffering — it reveals where it already lives, often hidden. It is a call for compassion, not condemnation.`,
  upright: [
    "Anxiety – overwhelmed by negative thoughts",
    "Insomnia – restlessness and worry",
    "Guilt – haunted by past actions or regrets",
    "Inner demons – facing your deepest fears",
    "Mental overload – too much to process alone"
  ],
  reversed: [
    "Release – letting go of inner torment",
    "Hope – light after emotional darkness",
    "Healing – confronting fears with support",
    "Self-forgiveness – releasing past burdens",
    "Perspective – realizing things aren't as bad as they seem"
  ],
  reading: `The Nine of Swords arrives when the mind becomes a storm. It reveals that you’ve been carrying heavy emotional burdens alone — and they have begun to take a toll. This is not a sign of weakness, but a cry for gentleness. The fears that visit you in the night are asking to be heard, not obeyed.

This card encourages you to separate the real from the imagined. Pain is real, yes, but it does not have to be eternal. You may need rest, help, or simply a breath of compassion. Allow yourself to be human. Let yourself be seen.

You are not broken. You are breaking open. From this vulnerable place, clarity will come — and with it, healing. The morning is closer than you think.

Ask yourself:
- What am I carrying in silence that I need to speak aloud?
- Is my mind telling stories that my soul doesn’t believe?
- Can I soften instead of harden when fear arises?

The Nine of Swords is not your sentence — it is your signal. You are stronger than the shadow.`,
  mirroring: ["eight-of-swords-tarot-card-reading-meaning", "three-of-swords-tarot-card-reading-meaning", "the-tower-tarot-card-reading-meaning"]
},

"ten-of-swords-tarot-card-reading-meaning": {
  slug: "ten-of-swords-tarot-card-reading-meaning",
  name: "Ten of Swords",
  subtitle: "Endings, Betrayal, Collapse",
  image: "/card/ten-of-swords-tarot-card-reading-meaning.webp",
  suit: "Swords",
  description: `The Ten of Swords shows a moment of utter collapse — where something has definitively ended, often painfully. This card symbolizes betrayal, loss, or burnout, yet also holds the seed of finality. While harsh, it brings a clear message: it is over. From this depth, new life can eventually rise.`,
  upright: [
    "End of a cycle – finality and closure",
    "Betrayal – feeling stabbed in the back",
    "Burnout – exhaustion from sustained struggle",
    "Rock bottom – nothing more to give",
    "Acceptance – realizing it's time to let go"
  ],
  reversed: [
    "Recovery – beginning to rise again",
    "Survival – learning from the collapse",
    "Release – letting go of the pain",
    "Perspective – seeing the lessons beneath the loss",
    "Hope – trusting the dawn after darkness"
  ],
  reading: `The Ten of Swords does not whisper — it shouts. It arrives when something has broken beyond repair. But within that breaking is a sacred truth: you have survived. You are here, breathing, reading these words — and that is proof of your strength.

This card asks you to stop trying to resurrect what has already fallen. It’s time to lay down the story, the guilt, the exhaustion. Yes, the pain was real. But so is your capacity to rise. The worst has happened. Now comes the stillness. And then — the rebirth.

Do not fear this ending. It has cleared a path. Where one cycle dies, another begins. Let yourself grieve, rest and then slowly — as if waking from a long night — begin again.

Ask yourself:
- What am I still clinging to that has already ended?
- How can I honor my pain without becoming it?
- What is quietly waiting to grow from this ending?

The Ten of Swords is your sacred bottom — but it is not your forever. The dawn is already reaching for you.`,
  mirroring: ["death-tarot-card-reading-meaning", "three-of-swords-tarot-card-reading-meaning", "the-star-tarot-card-reading-meaning"]
},

"page-of-swords-tarot-card-reading-meaning": {
  slug: "page-of-swords-tarot-card-reading-meaning",
  name: "Page of Swords",
  subtitle: "Curiosity, Truth-Seeking, Mental Clarity",
  image: "/card/page-of-swords-tarot-card-reading-meaning.webp",
  suit: "Swords",
  description: `The Page of Swords is a messenger of mental awakening — sharp, curious and relentlessly in pursuit of truth. This card represents the energy of someone who questions, observes and speaks with conviction. It encourages you to stay alert, seek knowledge and be unafraid to challenge ideas — including your own.`,
  upright: [
    "Curiosity – eager to learn and ask questions",
    "Clarity – cutting through confusion",
    "Alertness – quick-thinking and mentally sharp",
    "Truth-seeking – honesty and mental integrity",
    "New ideas – fresh perspective or approach"
  ],
  reversed: [
    "Gossip – spreading or receiving misinformation",
    "Impatience – rushing into conclusions",
    "Mental restlessness – scattered thoughts",
    "Overanalyzing – stuck in overthinking",
    "Immaturity – speaking without understanding"
  ],
  reading: `When the Page of Swords appears, it is the spark of inquiry — the invitation to observe, ask and challenge. You may feel called to seek answers or share ideas that have long lived in your mind. This card honors intellectual courage: the willingness to explore without fear, to speak even when your voice shakes.

Yet it also reminds you that truth is more than facts — it is intention, nuance and timing. The Page of Swords encourages boldness, yes, but also discernment. Are your words building bridges or drawing lines? Are you seeking knowledge to grow, or to win?

This card asks you to honor your mind as a sacred tool — sharpen it, don’t wield it carelessly. Speak when it matters. Listen when it counts. And remember: the greatest wisdom often begins with a question.

Ask yourself:
- What truth am I hungry to uncover or express?
- Am I listening as fiercely as I’m speaking?
- Is my curiosity rooted in growth or defensiveness?

The Page of Swords is your inner student — curious, courageous and ready to see with fresh eyes.`,
  mirroring: ["ace-of-swords-tarot-card-reading-meaning", "the-hermit-tarot-card-reading-meaning", "knight-of-swords-tarot-card-reading-meaning"]
},

"knight-of-swords-tarot-card-reading-meaning": {
  slug: "knight-of-swords-tarot-card-reading-meaning",
  name: "Knight of Swords",
  subtitle: "Determination, Bold Action, Unstoppable Drive",
  image: "/card/knight-of-swords-tarot-card-reading-meaning.webp",
  suit: "Swords",
  description: `The Knight of Swords charges forward with fierce clarity and intention. This card represents a phase of bold decision-making, swift movement and mental conviction. It may reflect your determination to pursue an idea or cause — or warn against acting without pause for reflection.`,
  upright: [
    "Bold action – swift and decisive moves",
    "Confidence – strong belief in your mission",
    "Focused drive – mentally sharp and purposeful",
    "Justice – standing up for truth or ideals",
    "Leadership – taking charge of a situation"
  ],
  reversed: [
    "Impulsiveness – charging ahead without reflection",
    "Aggression – forceful or argumentative energy",
    "Disregard – ignoring consequences or others’ input",
    "Burnout – pushing too hard, too fast",
    "Tunnel vision – losing sight of nuance"
  ],
  reading: `The Knight of Swords doesn’t wait — it acts. When this card appears, something within you is ready to move forward with speed and certainty. There’s fire in your intellect, conviction in your voice and urgency in your mission. This is not a time to second-guess; it is a time to trust your momentum.

And yet, this knight also serves as a mirror. Is your clarity truly grounded — or are you racing ahead out of fear of stillness? Sometimes, the fastest way forward is to slow down just enough to see the full path. Wisdom is not the absence of speed, but the presence of alignment.

Harness this energy with balance. Let your drive lead, but not trample. Speak with passion, but not at the expense of empathy. You are powerful now — make sure your power serves something sacred.

Ask yourself:
- What mission am I being called to pursue with boldness?
- Where do I need more clarity before taking action?
- Am I driven by truth — or by urgency to escape discomfort?

The Knight of Swords reminds you: speed without soul is chaos. Let your purpose lead the charge.`,
  mirroring: ["justice-tarot-card-reading-meaning", "the-chariot-tarot-card-reading-meaning", "page-of-swords-tarot-card-reading-meaning"]
},

"queen-of-swords-tarot-card-reading-meaning": {
  slug: "queen-of-swords-tarot-card-reading-meaning",
  name: "Queen of Swords",
  subtitle: "Truth, Independence, Clear Boundaries",
  image: "/card/queen-of-swords-tarot-card-reading-meaning.webp",
  suit: "Swords",
  description: `The Queen of Swords is a master of discernment — intelligent, honest and emotionally perceptive without being clouded by sentiment. She represents a mature form of clarity, where compassion and boundaries co-exist. This card invites you to speak truth, stand in integrity and cut through illusion with grace.`,
  upright: [
    "Clarity – seeing things for what they are",
    "Boundaries – emotional strength and protection",
    "Wisdom – intellect fused with life experience",
    "Authenticity – speaking truth without apology",
    "Independence – self-reliance and emotional maturity"
  ],
  reversed: [
    "Coldness – detaching too much from emotion",
    "Bitterness – carrying pain into current choices",
    "Judgment – being overly critical or harsh",
    "Isolation – keeping others at a distance",
    "Suppression – avoiding vulnerability"
  ],
  reading: `The Queen of Swords arrives when you are being called to lead with truth. Not just honesty with others, but the deeper honesty with yourself — the kind that asks you to name your needs, own your pain and choose what is real even when it’s hard. She carries both sword and sorrow — wisdom forged by experience.

This is not a cold card. It is a card of compassion with boundaries. You are being asked to see clearly and speak clearly — but to remember that clarity can still be kind. Let this queen guide you to clean communication, not sharp tongues; firm decisions, not closed hearts.

This is your invitation to hold your head high. To trust that your perspective matters. And to know that vulnerability and strength are not opposites — they are companions on the path to truth.

Ask yourself:
- Am I communicating my truth — or withholding to keep peace?
- Where do I need clearer boundaries in thought or emotion?
- How can I honor both my heart and my intellect?

The Queen of Swords reminds you: your voice is a sacred tool. Use it with clarity and care.`,
  mirroring: ["justice-tarot-card-reading-meaning", "king-of-swords-tarot-card-reading-meaning", "the-empress-tarot-card-reading-meaning"]
},

"king-of-swords-tarot-card-reading-meaning": {
  slug: "king-of-swords-tarot-card-reading-meaning",
  name: "King of Swords",
  subtitle: "Mental Mastery, Truth, Authority",
  image: "/card/king-of-swords-tarot-card-reading-meaning.webp",
  suit: "Swords",
  description: `The King of Swords stands as the embodiment of intellect, truth and ethical authority. He brings sharp focus and wise discernment to complex issues. This card suggests a time to lead with logic, maintain clarity under pressure and stand firmly for what is just — even if it's not easy.`,
  upright: [
    "Mental strength – wisdom grounded in experience",
    "Truth – unwavering commitment to what’s right",
    "Authority – respected leadership and clear decisions",
    "Clarity – ability to see through emotional fog",
    "Integrity – aligning thought, word and action"
  ],
  reversed: [
    "Tyranny – misuse of intellect or power",
    "Manipulation – twisting logic to suit ego",
    "Rigidity – unwillingness to see other perspectives",
    "Harsh judgment – cold or critical energy",
    "Emotional distance – neglecting the heart’s input"
  ],
  reading: `The King of Swords asks: what would it look like to lead with wisdom, not just knowledge? When this card appears, it calls on your highest mind — the one not swayed by drama, fear, or ego, but grounded in reason and ethical clarity. This is the seat of your inner sovereign.

You are being asked to rise above confusion, to cut through noise and to anchor your decisions in integrity. This king is not loud — he is precise. Not controlling — but commanding. He leads not just with answers, but with the courage to ask the right questions.

Let this card guide you toward just choices and conscious leadership. Speak the truth — and be brave enough to live it, even when it’s inconvenient. You are more capable than you know.

Ask yourself:
- Am I aligning my decisions with both truth and compassion?
- Where must I hold steady, even when challenged?
- Am I using my intellect to lead — or to defend?

The King of Swords invites you to embody wisdom not just in thought — but in presence and purpose.`,
  mirroring: ["justice-tarot-card-reading-meaning", "the-emperor-tarot-card-reading-meaning", "queen-of-swords-tarot-card-reading-meaning"]
},



"ace-of-pentacles-tarot-card-reading-meaning": {
  slug: "ace-of-pentacles-tarot-card-reading-meaning",
  name: "Ace of Pentacles",
  subtitle: "Prosperity, Foundations, Material Blessings",
  image: "/card/ace-of-pentacles-tarot-card-reading-meaning.webp",
  suit: "Pentacles",
  description: `The Ace of Pentacles symbolizes a divine offering of stability, security and material opportunity. It marks the fertile ground upon which dreams can be built — the seed of wealth, health, or home. This card appears when a fresh opportunity in the material world is being granted, often tied to career, finances, or physical well-being. It invites you to step through a portal of abundance with clarity and grounded intention.`,
  upright: [
    "New financial opportunity – job offer, investment, or career path",
    "Solid foundations – starting something stable and secure",
    "Prosperity – aligning with the energy of abundance",
    "Health and vitality – physical wellness and grounded strength",
    "Practical growth – long-term potential being planted"
  ],
  reversed: [
    "Missed opportunities – overlooking blessings at your doorstep",
    "Instability – shaky foundations or false starts",
    "Greed – attachment to material outcomes",
    "Lack of planning – failing to build long-term strategies",
    "Blocked abundance – self-sabotage or fear of success"
  ],
  reading: `When the Ace of Pentacles shows up in your reading, the universe is handing you a sacred key — one that opens the door to tangible prosperity. This is the seed of something real, something that grows with care, effort and consistency. Whether it's a new job, a financial breakthrough, or a healthier lifestyle, the foundation is now being laid.

You are not being asked to leap, but to plant. To nurture your plans like a gardener would tend to fertile soil — with presence, patience and purpose. Trust that what you begin now has the potential to blossom into a lasting source of stability. Every small step matters and every choice builds the path toward fulfillment in the physical realm.

This is not just about money or possessions — it’s about security, dignity and the life you are building brick by brick. Can you honor the sacredness of your effort? Can you commit to the long game? The Ace of Pentacles assures that you are being supported — now take that first grounded step.

Ask yourself:
- What am I ready to invest in physically, financially, or energetically?
- Do I believe I’m worthy of receiving material blessings?
- What structures or habits can help me build long-term stability?

When the Ace of Pentacles appears, prosperity is not a promise — it's a portal. Walk through it with faith and grounded determination.`,
  mirroring: ["the-empress-tarot-card-reading-meaning", "four-of-wands-tarot-card-reading-meaning", "ten-of-pentacles-tarot-card-reading-meaning"]
},

"two-of-pentacles-tarot-card-reading-meaning": {
  slug: "two-of-pentacles-tarot-card-reading-meaning",
  name: "Two of Pentacles",
  subtitle: "Balance, Priorities, Juggling Responsibilities",
  image: "/card/two-of-pentacles-tarot-card-reading-meaning.webp",
  suit: "Pentacles",
  description: `The Two of Pentacles represents the art of balancing multiple aspects of life — whether finances, relationships, or responsibilities. It reflects a period of adaptability, decision-making and dynamic energy. While things may feel in motion, this card encourages you to stay agile, flexible and focused, knowing that equilibrium is not fixed but constantly evolving.`,
  upright: [
    "Time management – handling several duties with grace",
    "Financial decisions – weighing pros and cons before investing",
    "Adaptability – flowing with change and uncertainty",
    "Multitasking – managing personal and professional spheres",
    "Maintaining balance – staying grounded amidst movement"
  ],
  reversed: [
    "Overwhelm – too much on your plate",
    "Disorganization – poor time or resource management",
    "Instability – dropping the ball in critical areas",
    "Avoidance – refusing to make necessary choices",
    "Burnout – emotional or financial fatigue"
  ],
  reading: `When the Two of Pentacles arrives, life may feel like a dance — one foot in work, the other in personal life; one hand in the future, the other juggling the now. This is not chaos, but choreography. And the question is: are you leading the rhythm, or are you being pulled by it?

You are being invited to cultivate conscious balance. To pause and assess where your energy is truly needed and where it’s being drained. There may be hard choices to make, or shifting priorities to acknowledge. But through presence and intentionality, you can transform overwhelm into alignment.

Balance is not perfection. It is fluid, forgiving and grounded in awareness. The Two of Pentacles reminds you that you have what it takes to manage this chapter — but only if you remain flexible, honest and willing to adjust your sails.

Ask yourself:
- What areas of my life are competing for my attention right now?
- Where am I overcommitting and where can I simplify?
- Am I making choices based on values, or merely reacting to demands?

When the Two of Pentacles appears, remember: your power lies not in doing it all, but in choosing wisely what to hold — and what to gently let go.`,
  mirroring: ["temperance-tarot-card-reading-meaning", "six-of-pentacles-tarot-card-reading-meaning", "seven-of-cups-tarot-card-reading-meaning"]
},

"three-of-pentacles-tarot-card-reading-meaning": {
  slug: "three-of-pentacles-tarot-card-reading-meaning",
  name: "Three of Pentacles",
  subtitle: "Teamwork, Mastery, Shared Goals",
  image: "/card/three-of-pentacles-tarot-card-reading-meaning.webp",
  suit: "Pentacles",
  description: `The Three of Pentacles is a card of collaboration, skill-building and unified effort. It reflects a moment when your talents are recognized and valued within a greater structure — be it a team, community, or shared project. It speaks to the power of combining individual strengths toward a collective vision.`,
  upright: [
    "Teamwork – working together for mutual success",
    "Skill development – honing your craft with focus",
    "Recognition – being appreciated for your contributions",
    "Shared goals – collaborating on a larger mission",
    "Solid foundations – early success built on planning"
  ],
  reversed: [
    "Lack of cohesion – disharmony or misaligned goals",
    "Poor craftsmanship – rushing without planning",
    "Ego clashes – difficulty working with others",
    "Unrecognized effort – feeling undervalued",
    "Disorganization – lack of structure or clear roles"
  ],
  reading: `When the Three of Pentacles enters your reading, it brings a sacred reminder: you are not meant to build everything alone. There is strength in community, in collaboration, in shared purpose. This is the architecture of dreams — each person bringing their stone to the cathedral of your becoming.

You are being called to both offer and receive support. To honor your craft and expertise while acknowledging that others bring gifts too. This card suggests that your path forward will be shaped not just by what you know, but by who you journey with — and how you build together.

This is a card of structure, mentorship and integrity. It invites you to bring your best, to communicate clearly and to remember: the masterpiece is not just the result, but the shared effort behind it.

Ask yourself:
- Where in my life am I being asked to collaborate or co-create?
- Am I recognizing the value of others — and allowing them to see mine?
- How can I build something sustainable with those around me?

When the Three of Pentacles appears, know this: great things are built not in solitude, but in symphony.`,
  mirroring: ["three-of-cups-tarot-card-reading-meaning", "the-emperor-tarot-card-reading-meaning", "eight-of-pentacles-tarot-card-reading-meaning"]
},

"four-of-pentacles-tarot-card-reading-meaning": {
  slug: "four-of-pentacles-tarot-card-reading-meaning",
  name: "Four of Pentacles",
  subtitle: "Control, Possession, Holding On",
  image: "/card/four-of-pentacles-tarot-card-reading-meaning.webp",
  suit: "Pentacles",
  description: `The Four of Pentacles represents the energy of control — often born from a fear of loss. Whether it’s clinging to money, status, beliefs, or emotional walls, this card warns against the trap of excessive guarding. It speaks to the tension between safety and stagnation and invites reflection on what you're holding and why.`,
  upright: [
    "Financial stability – creating a secure foundation",
    "Self-protection – setting healthy boundaries",
    "Conservation – being mindful with resources",
    "Discipline – control over desires and spending",
    "Possession – holding on tightly to what you value"
  ],
  reversed: [
    "Greed – hoarding out of fear",
    "Fear of loss – refusing to let go of what no longer serves",
    "Insecurity – overly dependent on material things",
    "Stagnation – resisting growth or vulnerability",
    "Isolation – emotional walls built too high"
  ],
  reading: `The Four of Pentacles asks: what are you gripping so tightly that you can no longer move? This card is a mirror for our relationship with control — and whether that control is truly serving us, or suffocating our expansion.

You may be protecting something valuable: your finances, your time, your heart. But in doing so, have you also closed the door to possibility? There is wisdom in boundaries, but there is also danger in walls too high. This card invites you to loosen your grip — to trust that security does not come from possession, but from presence.

Freedom begins where fear ends. And generosity, in all its forms, is a portal to both abundance and connection. Let the Four of Pentacles remind you that holding on too tightly can cost more than it protects.

Ask yourself:
- What am I afraid to lose — and why?
- Have I confused control with safety?
- How can I invite more trust and flow into this area of my life?

When the Four of Pentacles appears, remember: what is truly yours cannot be taken — and what you release often returns with grace.`,
  mirroring: ["five-of-pentacles-tarot-card-reading-meaning", "the-devil-tarot-card-reading-meaning", "seven-of-swords-tarot-card-reading-meaning"]
},

"five-of-pentacles-tarot-card-reading-meaning": {
  slug: "five-of-pentacles-tarot-card-reading-meaning",
  name: "Five of Pentacles",
  subtitle: "Hardship, Loss, Spiritual Poverty",
  image: "/card/five-of-pentacles-tarot-card-reading-meaning.webp",
  suit: "Pentacles",
  description: `The Five of Pentacles reflects a season of struggle — often financial, emotional, or spiritual. It reveals the feeling of being left out in the cold, of scarcity and abandonment. Yet it also points toward help that is near, if only we lift our heads to see it. This is a card of endurance, resilience and the quiet hope that flickers even in darkness.`,
  upright: [
    "Financial hardship – loss of income or security",
    "Emotional rejection – feeling isolated or abandoned",
    "Health challenges – physical or mental strain",
    "Fear of lack – scarcity mindset taking hold",
    "Asking for help – support is closer than it seems"
  ],
  reversed: [
    "Recovery – light returning after dark times",
    "Hope – rebuilding your sense of self-worth",
    "Support – receiving assistance or guidance",
    "Shifting mindset – releasing fear and shame",
    "Endurance – emerging stronger from the storm"
  ],
  reading: `The Five of Pentacles does not come to condemn — it comes to comfort. It knows the terrain of rock bottom, of empty pockets and heavier hearts. This card walks with you through the cold, not to dwell there, but to remind you that even here, you are not alone.

Pain may be real, but so is the hand reaching for you. So is the light in the window nearby. This is not a card of solutions, but of survival — and survival itself is sacred. If you are here, still breathing, still hoping — then you are already victorious in ways the world cannot measure.

Let this card remind you that support is closer than you think. That your worth is not measured by your wealth. That sometimes, the greatest transformation begins not with having more, but with believing you are enough — even now.

Ask yourself:
- Where have I felt abandoned or unsupported — and is that story still true?
- Am I willing to ask for help, or do I carry pain in silence?
- What beliefs about lack or worth am I ready to release?

When the Five of Pentacles appears, you are not forsaken. You are on the edge of rebirth — where brokenness becomes breakthrough.`,
  mirroring: ["four-of-pentacles-tarot-card-reading-meaning", "the-hermit-tarot-card-reading-meaning", "nine-of-swords-tarot-card-reading-meaning"]
},


"six-of-pentacles-tarot-card-reading-meaning": {
  slug: "six-of-pentacles-tarot-card-reading-meaning",
  name: "Six of Pentacles",
  subtitle: "Generosity, Reciprocity, Financial Balance",
  image: "/card/six-of-pentacles-tarot-card-reading-meaning.webp",
  suit: "Pentacles",
  description: `The Six of Pentacles symbolizes the delicate dance of giving and receiving. It highlights the importance of balance in resources, support and compassion. This card speaks to the flow of abundance—how what is shared from the heart returns in unseen ways. Whether you are in a position to offer help or seeking aid, the Six of Pentacles encourages fairness, humility and gratitude in all exchanges.`,
  upright: [
    "Generosity – freely giving resources, time, or wisdom",
    "Balance – a healthy exchange between giving and receiving",
    "Support – help offered or received during a time of need",
    "Compassion – aiding others without judgment",
    "Karma – what goes around, comes around in kind"
  ],
  reversed: [
    "Power imbalance – giving with strings attached",
    "Dependency – relying too heavily on others' generosity",
    "Greed – unwillingness to share or support others",
    "Resentment – feeling unappreciated for your efforts",
    "Conditional help – generosity used for control or manipulation"
  ],
  reading: `The Six of Pentacles asks you to observe the current balance of exchange in your life. Are you the giver or the receiver? And in either case, is the energy rooted in love—or ego? This card isn’t just about charity—it’s about the unseen value of support, acknowledgment and mutual respect.

If you're giving, do so without the need for praise or control. Your generosity should nourish the soul of another, not create silent debt. If you're receiving, allow yourself to accept with humility and openness, without shame. True giving does not diminish the giver and true receiving does not lessen the receiver.

This card reminds you that abundance is not only monetary—it is also emotional, spiritual and relational. What you share returns in unexpected forms. To live in flow, you must let yourself both pour and be poured into.

Ask yourself:
- Am I giving from a place of love, or seeking something in return?
- Do I accept help easily, or resist support out of pride?
- Where in my life do I need to restore balance in giving and receiving?

In the hands of the generous, blessings multiply. When the Six of Pentacles appears, it’s an invitation to restore harmony in the sacred exchange of life.`,
  mirroring: ["justice-tarot-card-reading-meaning", "the-empress-tarot-card-reading-meaning", "six-of-cups-tarot-card-reading-meaning"]
},

"seven-of-pentacles-tarot-card-reading-meaning": {
  slug: "seven-of-pentacles-tarot-card-reading-meaning",
  name: "Seven of Pentacles",
  subtitle: "Patience, Investment, Long-Term Growth",
  image: "/card/seven-of-pentacles-tarot-card-reading-meaning.webp",
  suit: "Pentacles",
  description: `The Seven of Pentacles reflects the moment of pause in a long journey. You have planted seeds, tended the soil and waited—now you assess what has grown. It is a card of quiet contemplation, urging you to consider the fruits of your labor, your long-term goals and whether your current path aligns with your deeper values.`,
  upright: [
    "Assessment – evaluating progress and results",
    "Patience – allowing growth to unfold in its time",
    "Investment – committed efforts for long-term return",
    "Reflection – measuring alignment with your vision",
    "Stewardship – nurturing what matters with care"
  ],
  reversed: [
    "Impatience – wanting results too quickly",
    "Wasted effort – energy spent on fruitless pursuits",
    "Doubt – questioning the value of your path",
    "Neglect – failing to tend to what you’ve started",
    "Burnout – overworking without joy or balance"
  ],
  reading: `This card whispers: pause. In the rush to build and achieve, you may overlook the importance of reflection. The Seven of Pentacles calls for a still moment to reassess. Not everything worth having comes quickly and not all growth is immediately visible. The roots often deepen before the blossom breaks through.

You are being asked to measure more than productivity—you are being asked to weigh fulfillment. Is what you're building meaningful? Are you investing your energy into something that truly feeds your spirit? This is not a time for rash decisions but for quiet recalibration.

Trust that every drop of effort, even unseen, contributes to something becoming. Let patience be your companion and clarity your reward. This card is a sacred pause before the next harvest.

Ask yourself:
- Am I aligned with the long-term rewards I seek?
- Where am I planting seeds that no longer inspire me?
- Can I trust that growth is occurring, even if I can’t yet see it?

When the Seven of Pentacles arrives, it honors your labor—but also urges you to be just as intentional about what comes next.`,
  mirroring: ["the-hermit-tarot-card-reading-meaning", "three-of-wands-tarot-card-reading-meaning", "knight-of-pentacles-tarot-card-reading-meaning"]
},

"eight-of-pentacles-tarot-card-reading-meaning": {
  slug: "eight-of-pentacles-tarot-card-reading-meaning",
  name: "Eight of Pentacles",
  subtitle: "Mastery, Craft, Dedication to Growth",
  image: "/card/eight-of-pentacles-tarot-card-reading-meaning.webp",
  suit: "Pentacles",
  description: `The Eight of Pentacles is the card of the apprentice, the artisan, the diligent soul who finds fulfillment through focus. It speaks of devotion to one’s craft, of learning through repetition and of honoring the process over the outcome. This card encourages steady growth through commitment and care.`,
  upright: [
    "Skill-building – developing mastery through practice",
    "Discipline – showing up daily with intention",
    "Commitment – dedicating yourself to a purpose",
    "Craftsmanship – taking pride in your work",
    "Progress – consistent steps toward your goal"
  ],
  reversed: [
    "Perfectionism – never feeling your work is good enough",
    "Monotony – losing passion through routine",
    "Lack of growth – stagnating despite effort",
    "Overwork – neglecting balance or rest",
    "Mediocrity – cutting corners or lacking passion"
  ],
  reading: `This card is a meditation on mastery. True excellence is not sudden; it is sculpted through time, repetition and soul-infused effort. The Eight of Pentacles reminds you that you are building more than a skill—you are building a version of yourself who can hold the weight of your dreams.

In the sacred rhythm of doing and redoing, of showing up with heart and humility, you become more than just productive—you become transformed. Whether your labor is artistic, intellectual, or spiritual, let your hands remember what your spirit longs to express.

Don’t rush this phase. The journey of mastery is holy. You are crafting something that matters—possibly even yourself.

Ask yourself:
- What am I devoted to building, even if it takes time?
- Do I honor the process, or only the outcome?
- Where in my life am I being called to greater consistency and care?

The Eight of Pentacles is your sign to keep going, keep refining and keep loving the process that shapes your purpose.`,
  mirroring: ["three-of-pentacles-tarot-card-reading-meaning", "the-emperor-tarot-card-reading-meaning", "knight-of-swords-tarot-card-reading-meaning"]
},

"nine-of-pentacles-tarot-card-reading-meaning": {
  slug: "nine-of-pentacles-tarot-card-reading-meaning",
  name: "Nine of Pentacles",
  subtitle: "Independence, Luxury, Self-Worth",
  image: "/card/nine-of-pentacles-tarot-card-reading-meaning.webp",
  suit: "Pentacles",
  description: `The Nine of Pentacles is a symbol of earned luxury and inner wealth. It speaks of a life cultivated through intention, discipline and grace. This card honors the rewards of self-sufficiency and reminds you that true abundance is not only material, but spiritual and emotional as well.`,
  upright: [
    "Self-reliance – standing strong in your independence",
    "Achievement – enjoying the fruits of your labor",
    "Elegance – grace and sophistication through effort",
    "Comfort – a life aligned with your values",
    "Fulfillment – satisfaction in your accomplishments"
  ],
  reversed: [
    "Isolation – success without connection",
    "Overindulgence – living beyond your means",
    "Superficiality – mistaking wealth for self-worth",
    "Imposter syndrome – doubting your deserved success",
    "Disconnection – having but not appreciating"
  ],
  reading: `This card is the garden you cultivated with your own hands. The Nine of Pentacles honors not just what you have, but who you've become in earning it. It asks you to breathe in the quiet elegance of your effort—without guilt, without comparison.

Independence does not mean loneliness. This card invites you to celebrate your sovereignty, to remember that creating a life of beauty and peace is a sacred art. You don’t need permission to enjoy what you’ve built.

At its heart, this card is about worthiness. You are allowed to savor your life. You are allowed to bloom.

Ask yourself:
- Have I given myself permission to feel proud of what I've built?
- Am I grounded in gratitude or distracted by what’s next?
- Do I recognize the emotional and spiritual richness in my life?

The Nine of Pentacles reminds you that luxury isn't always opulent—sometimes it’s the simple, sovereign joy of being content in your own skin.`,
  mirroring: ["queen-of-pentacles-tarot-card-reading-meaning", "the-empress-tarot-card-reading-meaning", "four-of-wands-tarot-card-reading-meaning"]
},

"ten-of-pentacles-tarot-card-reading-meaning": {
  slug: "ten-of-pentacles-tarot-card-reading-meaning",
  name: "Ten of Pentacles",
  subtitle: "Legacy, Stability, Generational Wealth",
  image: "/card/ten-of-pentacles-tarot-card-reading-meaning.webp",
  suit: "Pentacles",
  description: `The Ten of Pentacles represents the culmination of long-term effort—material security, family bonds and a life designed to endure. This card speaks to legacy, tradition and the foundations you build for the future. It honors stability earned through devotion and the blessings passed between generations.`,
  upright: [
    "Legacy – contributing to something that outlasts you",
    "Security – financial and emotional stability",
    "Family – deep roots and shared values",
    "Wealth – material success and comfort",
    "Fulfillment – living with purpose and continuity"
  ],
  reversed: [
    "Fragility – instability in legacy or finances",
    "Conflict – family disputes over money or values",
    "Short-sightedness – ignoring the long-term impact",
    "Disconnection – wealth without emotional depth",
    "Loss – financial or relational disruption"
  ],
  reading: `This is the card of enduring legacy. The Ten of Pentacles reminds you that what you build today echoes through time. It may be wealth, yes—but it may also be wisdom, values and the love you pass forward. The truest inheritance is not just assets, but presence.

You are being called to think beyond your lifetime. What kind of stability are you creating? How do your decisions ripple into the lives of others—now and in generations to come?

This card is not only about having—it is about holding, preserving and transmitting. Honor what has been handed to you. Cherish what you will one day hand to others.

Ask yourself:
- What am I building that will outlive me?
- Do I define wealth by possessions—or by legacy?
- Where in my life can I create more meaningful stability?

The Ten of Pentacles reminds you that your life is part of a greater tapestry—and your thread matters.`,
  mirroring: ["the-hierophant-tarot-card-reading-meaning", "king-of-pentacles-tarot-card-reading-meaning", "four-of-wands-tarot-card-reading-meaning"]
},
"page-of-pentacles-tarot-card-reading-meaning": {
  slug: "page-of-pentacles-tarot-card-reading-meaning",
  name: "Page of Pentacles",
  subtitle: "Ambition, Study, Grounded Dreams",
  image: "/card/page-of-pentacles-tarot-card-reading-meaning.webp",
  suit: "Pentacles",
  description: `The Page of Pentacles is the seeker of grounded growth. It represents a time of learning, planning and planting seeds for something meaningful. This card speaks to ambition shaped by humility and dreams rooted in discipline. It is the slow, intentional beginning of a path meant to last.`,
  upright: [
    "New ventures – starting a financial, educational, or personal project",
    "Discipline – focused effort and long-term planning",
    "Growth – learning new skills and building solid foundations",
    "Potential – seeing the first signs of a bigger vision",
    "Responsibility – committing to your own future"
  ],
  reversed: [
    "Procrastination – delaying plans or avoiding effort",
    "Lack of focus – drifting without structure or clarity",
    "Unrealistic goals – wanting results without the work",
    "Neglect – ignoring the details or foundations needed",
    "Insecurity – doubting your own abilities or value"
  ],
  reading: `The Page of Pentacles appears as a call to honor the beginning of something real. Not the daydream, but the quiet moment you roll up your sleeves and begin. Whether it’s your finances, studies, or a passion project—this card says the soil is ready.

It’s a reminder that success isn’t loud at first. It whispers. It grows in the background while you show up, day by day, with integrity and intention. Be curious. Be committed. Learn as you go. The Page asks you to take yourself seriously—not with pressure, but with care.

Every structure begins with a foundation. The Page of Pentacles encourages you to become the architect of your next chapter—sketching, shaping and believing in what’s possible.

Ask yourself:
- What small step can I take toward something lasting?
- Am I honoring my goals through action?
- Where am I being asked to learn, not rush?

The Page of Pentacles reminds you: true wealth begins with the courage to begin.`,
  mirroring: ["ace-of-pentacles-tarot-card-reading-meaning", "three-of-pentacles-tarot-card-reading-meaning", "the-magician-tarot-card-reading-meaning"]
},
"knight-of-pentacles-tarot-card-reading-meaning": {
  slug: "knight-of-pentacles-tarot-card-reading-meaning",
  name: "Knight of Pentacles",
  subtitle: "Diligence, Responsibility, Steady Progress",
  image: "/card/knight-of-pentacles-tarot-card-reading-meaning.webp",
  suit: "Pentacles",
  description: `The Knight of Pentacles represents persistence, loyalty and a grounded approach to long-term success. This card values routine, hard work and reliability. It signals a time when consistency and dedication matter more than speed or flash. The Knight is committed to finishing what they start—with care, patience and pride.`,
  upright: [
    "Commitment – showing up consistently for your goals",
    "Reliability – being dependable and trustworthy",
    "Diligence – sticking to routines that support progress",
    "Practicality – taking measured, thoughtful actions",
    "Persistence – refusing to give up, even when it’s slow"
  ],
  reversed: [
    "Stagnation – being stuck in routine or monotony",
    "Overworking – focusing too much on productivity",
    "Resistance – struggling with change or flexibility",
    "Neglect – failing to follow through on obligations",
    "Inertia – feeling uninspired or directionless"
  ],
  reading: `The Knight of Pentacles reminds you that the most enduring progress often comes from steady, silent effort. This card isn’t here to dazzle—it’s here to finish the race. It’s about honoring the process, even when it feels slow or unseen.

You may be in a season that demands repetition, discipline, or showing up without applause. But this isn’t wasted energy—it’s an investment. The Knight teaches you that there is power in patience and success in the details.

This is your invitation to move forward without rush. Trust the slow build. Keep your eyes on the path, your hands in the work and your heart anchored in purpose.

Ask yourself:
- Where in my life am I being asked to show more consistency?
- Am I honoring my commitments with care?
- What can I achieve if I stay steady and intentional?

The Knight of Pentacles reminds you: greatness is not always fast—but it is always faithful.`,
  mirroring: ["seven-of-pentacles-tarot-card-reading-meaning", "king-of-pentacles-tarot-card-reading-meaning", "eight-of-pentacles-tarot-card-reading-meaning"]
},
"queen-of-pentacles-tarot-card-reading-meaning": {
  slug: "queen-of-pentacles-tarot-card-reading-meaning",
  name: "Queen of Pentacles",
  subtitle: "Nurturing, Prosperity, Sacred Balance",
  image: "/card/queen-of-pentacles-tarot-card-reading-meaning.webp",
  suit: "Pentacles",
  description: `The Queen of Pentacles embodies the harmony between abundance and care. She is grounded, nurturing and deeply attuned to the physical and emotional needs of herself and others. This card speaks to stability earned through wisdom, generosity and intentional living. It invites you to lead with both practicality and heart.`,
  upright: [
    "Nurturing – caring for others while honoring your own needs",
    "Abundance – creating financial and emotional security",
    "Balance – managing responsibilities with grace",
    "Practical wisdom – making grounded, thoughtful choices",
    "Comfort – building a warm and safe environment"
  ],
  reversed: [
    "Neglect – putting others’ needs ahead of your own well-being",
    "Imbalance – struggling to manage home, work, or finances",
    "Overcontrol – micromanaging instead of trusting",
    "Insecurity – feeling unworthy of success or care",
    "Disconnection – losing touch with what truly matters"
  ],
  reading: `The Queen of Pentacles is the sacred steward of stability and care. When she appears, she asks you to embrace both the provider and the nurturer within. You are being called to create a life that is not only successful—but sustainable, beautiful and rooted in love.

This card honors the quiet power of tending—to your home, your finances, your relationships, your self. It speaks to the magic of showing up fully: not just chasing goals, but creating an ecosystem where growth feels safe. It invites you to slow down, tune in and lead with gentle strength.

True abundance is not hoarded—it’s shared. The Queen of Pentacles reminds you that success is richest when it is warm, inclusive and aligned with your values.

Ask yourself:
- Am I caring for my needs as deeply as I care for others?
- What does true prosperity look like in my daily life?
- Where can I create more harmony between doing and being?

The Queen of Pentacles reminds you: wealth is not just what you have—it’s how you hold it.`,
  mirroring: ["the-empress-tarot-card-reading-meaning", "ten-of-pentacles-tarot-card-reading-meaning", "nine-of-pentacles-tarot-card-reading-meaning"]
},

"king-of-pentacles-tarot-card-reading-meaning": {
  slug: "king-of-pentacles-tarot-card-reading-meaning",
  name: "King of Pentacles",
  subtitle: "Mastery, Wealth, Leadership with Integrity",
  image: "/card/king-of-pentacles-tarot-card-reading-meaning.webp",
  suit: "Pentacles",
  description: `The King of Pentacles represents success built through patience, wisdom and disciplined effort. He is a provider, protector and master of material realms. This card embodies mature leadership—one that values security, responsibility and the well-being of others. It is a sign of abundance earned through integrity.`,
  upright: [
    "Leadership – guiding others through wisdom and example",
    "Wealth – enjoying material success and stability",
    "Responsibility – honoring commitments and long-term vision",
    "Security – building strong foundations for self and others",
    "Support – offering guidance and tangible help to those in need"
  ],
  reversed: [
    "Greed – prioritizing wealth over values or relationships",
    "Control – using power in rigid or dominating ways",
    "Neglect – failing to support others emotionally or practically",
    "Materialism – defining worth by possessions alone",
    "Instability – fearing loss despite external success"
  ],
  reading: `The King of Pentacles calls you to step into a role of grounded authority. He does not chase—he builds. His success comes not from luck, but from loyalty to his values, his people and his purpose. When this card appears, you're being asked to lead with both confidence and care.

This is about more than wealth—it's about legacy. What are you creating that will last? How do you use your resources to uplift others, not just yourself? The King invites you to trust your maturity, your experience and your ability to create something meaningful.

Whether you're managing a business, a family, or your own future—the King of Pentacles reminds you to be generous, structured and rooted in what truly matters.

Ask yourself:
- What does responsible leadership look like in my life?
- Am I using my success to nurture others, or only myself?
- Where can I bring more integrity and intention to my work?

The King of Pentacles reminds you: true wealth is not just having—it’s holding it with honor.`,
  mirroring: ["the-emperor-tarot-card-reading-meaning", "knight-of-pentacles-tarot-card-reading-meaning", "ten-of-pentacles-tarot-card-reading-meaning"]
},

};
export default cardData;


